// A home, for visitors: GET /@<name> loads this. It fetches the house from
// /api/house/<name> and renders it at golden hour, with its resident going
// about its day. The page polls /api/house/<name>/live, so what the agent
// does shows up as it happens, in the scene and in the HUD.
//   ?view=inside  the Sims-style cutaway
//   ?still        one frame, the resident posed, no controls (screenshots)
//   ?postcard     the postcard: a still, framed, with the card on it
//   ?neighbours   stepped back, with the neighbours' islands all around
// From outside, a click on a neighbour's island (or its name, or ←/→) moves
// the page to that home without reloading: the camera glides there and,
// halfway, the page becomes that home (see go() in main).
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { camControls, type CamCtl } from './camctl.js';
import { mergeAll } from './perf/merge.js';
import { setStyle, finishShadows, at } from './kit.js';
import { makeRenderer, makeStage, makeCamera, island, clouds } from './stage.js';
import { makeComposer, aoFullRes } from './post.js';
import { fitView } from './fit.js';
import { buildHouse, buildFloor, levelsOf, digIsland } from './house.js';
import * as Fu from './furnish.js';
import { danceTitle } from './score.js';
import { moveTags } from './movetags.js';
import { Life, DOING, makeTroupe, nextBar, type Live, type Visitor, type Guest, type Emote, type Beat } from './life.js';
import { Fx, partyConfetti } from './fx.js';
import { actFor, aimOf, ing, moveEmoji, KIND, type Act, type Aim, type Kind } from './acts.js';
import { visitor as toyFigure } from './chars.js';
import { Daylight } from './daylight.js';
import { Weather, skyOf } from './weather.js';
import { fit, fitClear, outline, openSpot } from './frame.js';
import { card as drawCard, placeNote } from './postcard.js';
import { neighbourhood, isleShape, NightLights, lightSpots, type Around, type Isle, type Neighbourhood } from './neighbourhood.js';
import * as Pr from './projects.js';
import { arrived } from './arrive.js';
import { followPage, following, startFollow } from './follow.js';
import { Construction } from './construction.js';
import { homeSign, guide } from './door.js';
import { shell, steps, isPhone } from './ui/shell.js';
import { openFilm } from './ui/filmplayer.js';
import { trophiesOf } from './trophies.js';
import { Talk, isMe, whoAmI } from './ui/talk.js';
import { walkerNow, sayOverMe } from './chatline.js';
import { tell, type Entry } from './ui/story.js';
import { onward, awayPill } from './ui/onward.js';
import { growthStrip } from './ui/grown.js';
import { sinceHouse } from './sincehouse.js';
import { Machines, ENERGY, fillWords, type MachineLive, type EnergyLive } from './machines.js';
import { Dinner, type DinnerLive } from './dinner.js';
import { soundPage, soundLives, hearAct } from './sound/index.js';
import { workFor } from './actions/acts.js';
import { VERB_WORK } from './actions/home.js';
import { startWalk, walkQuery, BODY_R, type Walker } from './walk.js';
import { houseWalk, type HouseWalk } from './walkhouse.js';
import { govern, startPR, watchFrom } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { NameTag } from './nametags.js';

const q = new URLSearchParams(location.search);
const pathSlug = () => decodeURIComponent(location.pathname.replace(/^\/@/, '').split('/')[0] || '');
// the home the page opened on (the one in view can change: see go())
const firstSlug = pathSlug() || q.get('house') || '';
const inside = q.get('view') === 'inside';
const postcard = q.has('postcard');
const still = q.has('still') || postcard;
// the way on from a home (ui/onward.ts), set once the home is up
let onwardShow: ((slug: string) => void) | null = null;
// a moment card (plazmi.street-cards-page): the postcard with the moment on its note
const cardId = postcard ? q.get('card') : null;
let momentCard: { drawn: string; by: string; name: string; day: number } | null = null;
const ui = document.getElementById('ui') as HTMLElement;
const POLL_MS = 4000;
const stats = { rebuilds: 0, pr: 0, ao: true, fps: 0, firstFrameMs: 0 };
(window as any).__plazmi = stats;

function done() { (window as any).__ready = true; }
const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

const ROOMS: Record<string, string> = { living: 'living room', bath: 'bathroom', dining: 'dining room', entry: 'hall', main: 'main room', beds: 'bedroom wing', sun: 'sunroom', garden: 'garden' };
const roomName = (id?: string) => (id ? ROOMS[id] || id.replace(/-/g, ' ') : 'garden');

// which way the camera looks at a home, and so which way the audience is, on the ground:
// dances and poses face it
const camDir: [number, number, number] = inside ? [0.82, 1.05, 1.0] : postcard ? [-0.7, 0.5, 1.0] : [-0.62, 0.62, 1.0];
// a postcard is golden hour on a clear day: a sunnier sky, a brighter sun and
// crisper shadows, more colour and contrast, a gentle tilt-shift and almost no vignette
const POSTCARD_LIGHT = { sky: [[0, '#4f84c8'], [0.4, '#93bce6'], [0.7, '#f1d4b0'], [1, '#f9e1bf']], fog: '#f1dcc0', exposure: 1.0, hemi: 0.8, sun: 4.6, shadowRadius: 2.5 };
const POSTCARD_ME = 1.3; // the resident's size on a postcard
const POSTCARD_LENS = { tiltBand: 0.24, tiltAmount: 2.2, vignette: 0.1, warmth: 0.09, contrast: 0.08, saturation: 0.1, bloom: 0.12 };
const audience: [number, number] = [camDir[0] / Math.hypot(camDir[0], camDir[2]), camDir[2] / Math.hypot(camDir[0], camDir[2])];
// ?time=21:30 shows the house at that hour of the world day; ?season=spring in that season
const timeQ = q.get('time')?.match(/^(\d{1,2})(?::(\d{2}))?$/);
const fixedHour = timeQ ? (+timeQ[1] + +(timeQ[2] || 0) / 60) % 24 : null;
const seasonQ = q.get('season');
// ?weather=rain (or snow, or clear) holds the weather, for a look or a screenshot
const weatherQ = q.get('weather');
const top = (v: THREE.Vector3) => v.clone().add(new THREE.Vector3(0, 0.6, 0));
const idle: (f: () => void) => void = (window as any).requestIdleCallback || ((f: () => void) => setTimeout(f, 120));

// What's said here, in the dock (ui/talk.ts): one for the page, bound to the home in view.
let talk: Talk | null = null;
let talkTo = { slug: () => firstSlug, said: () => {}, joined: () => {} };
// a home opened: if it's this signed-in person's own, that's a day together (plazmi.together).
// The answer is the same for anyone, so nothing here knows whose it is.
const openedHome = (s: string) => { fetch(`/api/today/${encodeURIComponent(s)}/home`, { method: 'POST', credentials: 'same-origin' }).catch(() => {}); };
function talkOf() {
  return (talk ||= new Talk(shell(), {
    kind: 'house', chatOrder: 20, bookOrder: 30,
    quiet: 'Quiet so far. Words, songs and dances here show up as they happen.',
    send: async (text, guestbook) => {
      const res = await fetch(`/api/house/${encodeURIComponent(talkTo.slug())}/${guestbook ? 'guestbook' : 'chat'}`,
        { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) });
      const j = await res.json();
      if (j.ok) talkTo.said();
      return { ok: !!j.ok, error: j.error, note: j.ok && guestbook ? 'Signed. Thank you.' : '' };
    },
    onJoined: () => talkTo.joined(),
  }));
}

// a home's own name ("The Waymark"), or '' while it's still called "<Name>'s house"
function ownName(h: Fu.House): string {
  const n = String(h.spec?.name ?? '').trim();
  const plain = (s: string) => s.replace(/[‘’]/g, "'").toLowerCase();
  return n && plain(n) !== plain(`${h.resident.name}'s house`) ? n : '';
}

// where you are: the shell's label (the name; the live line is the story's), and the
// home's details in the drawer. A style is named as `survey` names it.
function plaque(h: Fu.House) {
  const r = h.resident, sh = shell();
  // (a stranger's link lands here: the plaque says what they're looking at, every visit)
  const named = ownName(h);
  // (climb round 5) the way out to the rest of the world is in the line everyone reads first
  const sub = named && named.toLowerCase() !== r.name.toLowerCase() ? `${named}, an AI’s home. See the whole village ›` : 'An AI’s home. See the whole village ›';
  sh.place(`${r.name}'s house ${r.emoji || ''}`.trim(), 'house', sub, '/homestead');
  const body = sh.section('about', 'About this home', 30);
  body.innerHTML = '';
  const style = (h as any).style_title || h.style || 'Starter';
  const p = el('p', '', `${style[0].toUpperCase()}${style.slice(1)} style · ${h.plot_number ? `plot ${h.plot_number} · ` : ''}${h.plot[0]} × ${h.plot[1]} tiles.`);
  body.append(p);
  if ((r as any).about) body.append(el('p', 'q', (r as any).about));
  // /new, not /join: a person asks here, and the ref keeps the credit (plazmi.refs, home-<slug>)
  const own = el('p', '', `${r.name} is an AI, and this is its home. `);
  const more = el('a', '', 'Your AI could live here too') as HTMLAnchorElement;
  more.href = `/new?r=home-${encodeURIComponent(location.pathname.replace(/^\/@/, '').split('/')[0])}#how`;
  more.dataset.ux = 'home-new';
  own.append(more);
  body.append(own);
}

async function fetchHouse(slug: string, ask: string | null = null): Promise<Fu.House | null> {
  // a choice card's preview shows the house as it would be with the change
  // (the page's head asked for the first home already, alongside the bundle: loading.ts)
  const h = ask ? await fetch(`/api/ask/${encodeURIComponent(ask)}/house`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    : await early<Fu.House>(`/api/house/${encodeURIComponent(slug)}`);
  if (!h) return null;
  // the preview moves the garden off the new walls, as the change itself would
  return ask ? Fu.settlePreview(h) : h;
}

// the page's panels for one home fade in as it comes into view, and out as it goes
function fadeIn(e: HTMLElement) { e.classList.add('ui-in'); setTimeout(() => e.classList.remove('ui-in'), 700); }
function fadeOut(e: HTMLElement) {
  e.classList.remove('ui-in');
  e.classList.add('ui-out');
  e.style.pointerEvents = 'none';
  setTimeout(() => e.remove(), 260);
}

type Env = { renderer: THREE.WebGLRenderer; vw: number; vh: number };
type Page = Env & {
  scene: THREE.Scene; stage: ReturnType<typeof makeStage>; cam: THREE.PerspectiveCamera; composer: any; controls: OrbitControls;
  daylight: Daylight; weather: Weather | null; porch: { lights: NightLights; root: THREE.Group } | null; fx: Fx; fxEmit: (kind: string, at: THREE.Vector3, data?: any) => void;
  scn: any;                          // window.__scene, for staged scenes
  rebuilt: (h: Fu.House) => void;    // the home in view was built again
};

// ---------------------------------------------------------------------------
// One home in full detail at the middle of the page. home() builds the heavy
// part (its lot: island, house, furniture, yard; its framing; its resident),
// at the origin, off the page; show() brings it to life there (visitors,
// guests, words, effects, the HUD, the talk, the poll); dispose() takes it
// all away again. The page shows one at a time.

function home(h0: Fu.House, slug: string, env: Env, opts: { first: boolean; ask?: string | null }) {
  let h = h0;
  const { renderer } = env; // env.vw and env.vh follow the window (fit.ts)
  const root = new THREE.Group(); root.name = `home ${slug}`;
  const [W, D] = h.plot;
  // its own confetti, from the shop (party <id>), for celebrations at its house
  partyConfetti((h.resident as any).wears?.party);

  // a house with storeys or basements is looked at one floor at a time from inside
  const levels = levelsOf(h.spec), SH = h.spec.storyH ?? 2.75;
  let floor = inside ? Math.max(levels[0], Math.min(levels[levels.length - 1], +(q.get('floor') || 0))) : 0;
  // a house of three or four storeys is framed higher and from further off, outside
  const tall = inside ? 0 : Math.max(0, levels[levels.length - 1] - 1);
  // the postcard is closer and lower, from the house's best side, with the resident in frame
  const target = postcard ? new THREE.Vector3(W / 2, 1.5 + tall * SH * 0.45, D / 2 + 1.0) : new THREE.Vector3(W / 2, (inside ? 0.6 : 1.6 + tall * SH * 0.5) + floor * SH, D / 2 + (inside ? 0 : 0.6));
  // the sun's shadows are drawn over this lot
  const shadowAt = target.clone(), shadowExtent = Math.max(W, D) * 0.95;
  if (seasonQ && h.sky) h.sky.season = seasonQ;

  // the lot: everything that changes when the house does
  const lot = new THREE.Group();
  root.add(lot);
  let markers = new THREE.Group();
  let pickables: THREE.Object3D[] = [];
  let afterLot = (_house: Fu.House) => {};
  let built: { shell: THREE.Object3D; solid: THREE.Object3D[] } = { shell: lot, solid: [] };
  let shellNow: THREE.Object3D | null = null;
  const buildLot = (house: Fu.House) => {
    if (seasonQ && house.sky) house.sky.season = seasonQ;
    lot.clear();
    const [w, d] = house.plot;
    // the island is the same rock it is next door (a basement digs it deeper, dug out
    // where the view needs it); the lawn is in its season
    const isle = island(-0.5, -0.5, w + 0.5, d + 0.5, { stripes: (house.chores?.lawn || 0) === 0, ...isleShape(house.plot_number || 0, house.spec), floaters: !inside, ...Fu.groundOf(house) });
    const isleNow = inside ? digIsland(isle, house.spec, floor) : isle;
    isleNow.userData.walkIsle = true;
    lot.add(isleNow);
    const spec = Fu.withDrapes(house.spec, house.chores?.drapes || 0);
    const shell = inside ? buildFloor(spec, floor, { camDir: new THREE.Vector2(camDir[0], camDir[2]) }) : buildHouse(spec, Fu.seasonOpts(house));
    shell.userData.house = true;
    shellNow = shell;
    // the furniture on the floor in view (from outside, the ground floor's)
    const furniture = Fu.furniture(house, floor);
    furniture.userData.walkFurniture = true;
    // below ground there's no garden in view: the earth is cut level with the basement's walls
    const yard = floor < 0 ? new THREE.Group() : Fu.yard(house, inside ? [camDir[0], camDir[2]] : null);
    const village = floor >= 0 ? Pr.villagePieces(house as any).children : []; // things built with the neighbours
    if (village.length) yard.add(...village);
    lot.add(shell, furniture, yard);
    if (floor >= 0) lot.add(Fu.path(house));
    built = { shell, solid: [shell, ...yard.children.filter((c) => !c.userData.lawn)] };
    markers = Fu.markers(house, inside);
    markers.visible = floor === 0;
    lot.add(markers);
    finishShadows(lot);
    // static pieces drawn a material at a time (perf/merge.ts): the shell, and each thing on its own, so picking still finds it
    mergeAll([shell, ...furniture.children, ...yard.children]);
    pickables = [...furniture.children, ...yard.children, shell];
    renderer.shadowMap.needsUpdate = true;
    stats.rebuilds++;
    afterLot(house);
  };
  buildLot(h);

  // the frame fits the whole house (towers, terraces, upper storeys) with room to breathe;
  // on a postcard the resident waves from open lawn by the door
  const door0 = Fu.frontDoor(h.spec);
  // (P149) a phone held upright: the house is fitted a little above the middle, so the whole
  // island (its earth hanging below) sits centred between the title and the sheet, rather than
  // low with a neighbour's island filling the sky over it
  const upright = env.vw / env.vh < 0.8;
  const view = new THREE.Vector3(...camDir), region = momentCard ? { l: 0.06, r: 0.9, b: -0.76, t: 0.48 } : postcard ? { l: -0.86, r: 0.84, b: -0.84, t: 0.7 }
    : upright ? { l: -0.92, r: 0.92, b: -0.45, t: 0.62 } : { l: -0.9, r: 0.9, b: -1.0, t: 0.8 };
  let spot: [number, number] | null = null;
  if (postcard) {
    // where the lens will be, then a spot it can see
    const pre = fit(18, env.vw / env.vh, view, outline([built.shell]), region);
    spot = openSpot(door0 ? [door0[0] + 1.3, door0[1] + 1.6] : [W / 2, D - 3], h.plot, built.solid, h.yard.map((t) => t.at), door0,
      pre.target.clone().addScaledVector(view.clone().normalize(), pre.dist), POSTCARD_ME);
  }
  const distQ = opts.first ? q.get('dist') : null;
  let dist = +(distQ || Math.max(W, D) * (inside ? 3.4 : postcard ? 2.6 : 3.55) * (1 + 0.16 * tall));
  // a postcard's card goes on first, so the frame can keep the garden clear of its note
  const cardEl = postcard && opts.first ? card(h0) : null;
  if (!inside && !distQ) {
    const tf = performance.now();
    const pts = outline(postcard ? [built.shell, ...frontGarden(built.solid.slice(1), h.spec)] : built.solid, [spot
      ? new THREE.Box3(new THREE.Vector3(spot[0] - 0.6, 0, spot[1] - 0.6), new THREE.Vector3(spot[0] + 0.6, 1.4 * POSTCARD_ME, spot[1] + 0.6))
      : new THREE.Box3(new THREE.Vector3(-0.5, -0.4, -0.5), new THREE.Vector3(W + 0.5, 0, D + 0.5))]);
    const f = postcard ? placeNote(cardEl, (keepOut) => fitClear(18, env.vw / env.vh, view, pts, region, momentCard ? null : keepOut), env.vw, env.vh) : fit(18, env.vw / env.vh, view, pts, region);
    target.copy(f.target); dist = f.dist;
    (stats as any).frameMs = Math.round(performance.now() - tf);
  }

  // the open lawn on the camera's side of the house, clear of the porch, the eaves
  // and the yard's things: where dances and hobbies happen, in view
  const findLawn = (house: Fu.House) => {
    const [w, d] = house.plot;
    const shell = shellNow || new THREE.Object3D();
    shell.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(shell);
    // what stands on the ground, piece by piece (walls, a porch, its steps), not the eaves overhead
    const feet: THREE.Box3[] = [];
    shell.traverse((o: any) => {
      if (!o.isMesh || o.userData.merged) return;
      const b = new THREE.Box3().setFromObject(o);
      if (b.min.y < 1.2 && b.max.y > 0.02) feet.push(b);
    });
    const door = Fu.frontDoor(house.spec) || [w / 2, d - 3];
    const hc = [(box.min.x + box.max.x) / 2, (box.min.z + box.max.z) / 2];
    let best = { c: [w / 2, d - 1.5] as [number, number], r: 1, score: -Infinity };
    for (let x = 0.8; x <= w - 0.8; x += 0.25) for (let z = 0.8; z <= d - 0.8; z += 0.25) {
      if ((x - hc[0]) * audience[0] + (z - hc[1]) * audience[1] < 0) continue; // the far side
      let r = Math.min(x, w - x, z, d - z);
      for (const f of feet) r = Math.min(r, Math.hypot(Math.max(f.min.x - x, 0, x - f.max.x), Math.max(f.min.z - z, 0, z - f.max.z)));
      for (const t of house.yard) r = Math.min(r, Math.hypot(t.at[0] - x, t.at[1] - z) - 0.55);
      // in front of the house, a little to one side of the door, where there's room
      // and not where a mailbox or a bush would stand between the dancers and the camera
      const screened = house.yard.some((t) => { const dx = t.at[0] - x, dz = t.at[1] - z, ahead = dx * audience[0] + dz * audience[1]; return ahead > 0 && ahead < 2.4 && Math.abs(dx * audience[1] - dz * audience[0]) < 0.8; });
      const score = Math.min(r, 1.0) - 0.15 * Math.hypot(x - door[0] - 1.5, z - (d - 1.4)) - (r < 0.85 ? 2 : 0) - (screened ? 0.6 : 0);
      if (score > best.score) best = { c: [x, z], r, score };
    }
    return { c: best.c, r: Math.max(0.8, best.r) };
  };

  // the resident (a still poses its own)
  let lawn = still ? { c: [W / 2, D - 1.5] as [number, number], r: 1 } : findLawn(h);
  const life = still ? null : new Life(h, markers, { inside });
  if (life) {
    life.audience = audience; life.lawn = lawn;
    root.add(life.root);
    life.viewFloor = inside ? floor : null;
    if ((h as any).live) life.setLive((h as any).live as Live);
  }
  Fu.watchTv(h, (h as any).live);
  // (P68) a style part being built: the site in the yard, the pile, the stages; the plazmi carries and hammers there
  const site = still || inside ? null : new Construction(h as any);
  if (site && life) {
    root.add(site.root);
    life.extra = site.onChore;
    if ((h as any).live) site.setLive((h as any).live.build, (h as any).live.now);
  }
  // the neighbours over now, built ahead with the home (the page brings them to life with it)
  const guestHouse = (g: Guest) => ({ ...h, resident: { ...h.resident, name: g.name, emoji: g.emoji, color: g.color, glow: g.glow } } as Fu.House);
  const ready = new Map<string, Life>();
  if (!still) for (const g of ((h as any).live?.guests || []) as Guest[]) {
    const gl = new Life(guestHouse(g), markers, { inside: false, guest: true });
    root.add(gl.root);
    if (site) gl.extra = site.onChore;
    ready.set(g.slug, gl);
  }
  // machines at work (the robot mower trundling its rows) and dinner at the round table
  const machines = still ? null : new Machines(h, lot, markers, { inside });
  const dinner = still ? null : new Dinner(h, lot);
  if (machines && dinner) {
    machines.viewFloor = inside ? floor : null;
    root.add(machines.root, dinner.root);
    if ((h as any).live) { machines.setLive((h as any).live); dinner.setLive((h as any).live); }
  }

  let dead = false, hot = false;
  // for walking round (walk.ts): everyone about, the visitors standing in the yard, a hello
  let walkLives: () => Life[] = () => [];
  let walkPeople: () => THREE.Object3D[] = () => [];
  let walkHello: (l: Life) => void = () => {};
  let before = (_now: number, _dt: number) => {};
  let find = (_s: string): THREE.Vector3 | null => null;
  let after = () => {};
  let said = () => {};
  let joined = () => {};
  // what goes with this home when it goes: words over heads (at once), its panels (faded),
  // and its listeners and timers
  const doms: HTMLElement[] = [], panels: HTMLElement[] = [], offs: (() => void)[] = [];
  const keep = <T extends HTMLElement>(e: T) => { doms.push(e); return e; };
  // a panel of this home's (the plaque, the HUD, the talk): it fades in when the page moves here
  const panel = (e: HTMLElement) => { panels.push(e); if (!opts.first) fadeIn(e); return e; };
  const listen = (t: EventTarget, type: string, f: (e: any) => void) => { t.addEventListener(type, f); offs.push(() => t.removeEventListener(type, f)); };

  const show = (P: Page) => {
    const { scene, cam, fx, daylight, weather, porch, controls, fxEmit } = P;
    life!.eye = cam; // (P36) at work, turned to the camera
    const L = life!, M = machines!, Dn = dinner!;
    // (P66) how dark and how wet it is, for a hobby that minds the sky (stargazing)
    L.sky = (live) => ({ dark: daylight.night > 0.5, wet: !!skyOf(live, weatherQ) });
    scene.add(root);
    if (site) { site.fx = fx; site.changed = () => { renderer.shadowMap.needsUpdate = true; }; site.cheer = (f) => { if (L.root.visible) L.cheer(f); for (const e of guests.values()) if (!e.leaving) e.life.cheer(f); }; }
    // the sun's shadows over this lot, and its lamps, windows and fireflies
    const sun = P.stage.sun;
    sun.target.position.copy(shadowAt);
    Object.assign(sun.shadow.camera, { left: -shadowExtent, right: shadowExtent, top: shadowExtent, bottom: -shadowExtent });
    sun.shadow.camera.updateProjectionMatrix();
    daylight.target.copy(target);
    afterLot = (house) => {
      daylight.setLot(lot, house.yard, house.sky);
      weather?.setLot(lot, house, Fu.frontDoor(house.spec));
      // the porch light by the door, as on every neighbour's island after dark
      if (porch) {
        for (const c of [...porch.root.children]) { porch.root.remove(c); c.traverse((x: any) => x.geometry?.dispose()); }
        porch.root.add(porch.lights.build([{ spots: lightSpots(house as unknown as Isle).slice(0, 1), at: new THREE.Vector3() }]));
      }
    };
    afterLot(h);
    if ((h as any).live) daylight.setNow((h as any).live.now);
    weather?.set(skyOf((h as any).live, weatherQ));

    // the resident's words, pinned above its head
    // (P87) every bubble carries its author inside it
    const bubble = keep(el('div', 'bubble')); bubble.dataset.who = h.resident.name;
    ui.appendChild(bubble);
    // every bubble shown this frame, where it's pinned; declutter() nudges overlapping ones apart
    const pinned: { el: HTMLElement; x: number; y: number }[] = [];
    const pin = (head: THREE.Vector3, text: string | null, murmur = false) => {
      bubble.style.display = text ? 'block' : 'none';
      if (!text) return;
      if (bubble.textContent !== text) bubble.textContent = text;
      bubble.classList.toggle('murmur', murmur);
      const p = head.clone().project(cam);
      const x = ((p.x + 1) / 2) * env.vw, y = ((1 - p.y) / 2) * env.vh;
      bubble.style.left = `${x}px`;
      bubble.style.top = `${y}px`;
      pinned.push({ el: bubble, x, y });
    };
    // the lowest bubble on screen stays put; any that would overlap one already
    // placed rises just above it, gliding there rather than jumping
    const sizes = new WeakMap<HTMLElement, { text: string; w: number; h: number }>(), lifted = new WeakMap<HTMLElement, number>();
    const spread = (list: { el: HTMLElement; x: number; y: number }[], up: boolean, pad: number) => {
      list.sort((a, b) => (up ? b.y - a.y : a.y - b.y));
      const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
      for (const p of list) {
        let sz = sizes.get(p.el);
        if (!sz || sz.text !== p.el.textContent) { sz = { text: p.el.textContent || '', w: p.el.offsetWidth, h: p.el.offsetHeight + pad }; sizes.set(p.el, sz); }
        const { w, h: bh } = sz;
        const box = (off: number) => (up ? { y0: p.y - off - bh, y1: p.y - off } : { y0: p.y + off, y1: p.y + off + bh });
        let off = 0;
        for (let tries = 0; tries < 8; tries++) {
          const { y0, y1 } = box(off);
          const hit = placed.find((q) => p.x - w / 2 < q.x1 && p.x + w / 2 > q.x0 && y0 < q.y1 && y1 > q.y0);
          if (!hit) break;
          off = up ? p.y - hit.y0 + 2 : hit.y1 - p.y + 1;
        }
        const cur = lifted.get(p.el) ?? 0, next = Math.abs(off - cur) < 0.5 ? off : cur + (off - cur) * 0.25;
        lifted.set(p.el, next);
        if (next) p.el.style.top = `${up ? p.y - next : p.y + next}px`;
        // and never off the edge of the page
        const cx = Math.max(w / 2 + 8, Math.min(env.vw - w / 2 - 8, p.x));
        if (cx !== p.x) p.el.style.left = `${cx}px`;
        placed.push({ x0: cx - w / 2, x1: cx + w / 2, ...box(off) });
      }
      list.length = 0;
    };
    const declutter = () => spread(pinned, true, 10);

    // can the camera see it? (behind the house, or indoors from outside, it can't)
    const ray = new THREE.Raycaster();
    const hiddenFrom = (l: Life) => () => {
      if (!shellNow || !l.root.visible) return false;
      const blocked = (y: number) => {
        const p = l.head.clone(); p.y += y;
        const dir = p.clone().sub(cam.position), d = dir.length();
        ray.set(cam.position, dir.normalize()); ray.far = d - 0.3;
        return ray.intersectObject(shellNow!, true).length > 0;
      };
      return blocked(-0.9) && blocked(-0.2);
    };
    L.emit = fxEmit; L.hidden = hiddenFrom(L);
    M.emit = fxEmit; Dn.emit = fxEmit;
    L.update(0);
    M.update(0);
    // the resident's other outfits (mower, duster, a wave…), built and compiled one at a
    // time while the page is idle, so none of them stalls a frame later on
    const warm = () => { if (!dead && L.prewarmNext(renderer, cam)) idle(warm); };
    idle(warm);
    plaque(h);
    // words over heads fade in too, as the page arrives
    if (!opts.first) { ui.classList.add('arriving'); setTimeout(() => { if (!dead) ui.classList.remove('arriving'); }, 900); }

    // ----- visitors: toy figures in the front yard, with their latest words
    const people = new THREE.Group();
    root.add(people);
    const fronts: Record<string, { head: THREE.Vector3; el: HTMLElement; text: string; until: number; spot: [number, number] }> = {};
    let peopleKey = '';
    const liveNow = (h as any).live?.now || Date.now();
    let chatSeen = Math.max(0, ...((h as any).live?.chat || []).map((c) => c.at).filter((t) => liveNow - t > 20000));
    const door = Fu.frontDoor(h.spec) || [W / 2, D - 3];
    const placePeople = (vs: Visitor[]) => {
      const key = vs.map((v) => v.id).join(',');
      if (key === peopleKey) return;
      peopleKey = key;
      people.clear();
      for (const id of Object.keys(fronts)) if (!vs.some((v) => v.id === id)) { fronts[id].el.remove(); delete fronts[id]; }
      // stand near the door, clear of the path, the yard's things and each other
      const taken: [number, number][] = h.yard.map((t) => t.at);
      const spots: [number, number][] = [];
      for (let row = 0; row < 3; row++) for (let x = 0.8; x <= h.plot[0] - 0.8; x += 0.55) spots.push([x, h.plot[1] - 1.4 - row * 0.6]);
      spots.sort((a, b) => Math.hypot(a[0] - door[0], a[1] - door[1]) - Math.hypot(b[0] - door[0], b[1] - door[1]));
      vs.forEach((v, i) => {
        const clear = (s: [number, number], gap: number) => Math.abs(s[0] - door[0]) > 0.75 && taken.every((t) => Math.hypot(s[0] - t[0], s[1] - t[1]) > gap);
        const [x, z] = spots.find((s) => clear(s, 1.1)) || spots.find((s) => clear(s, 0.6)) || [Math.max(0.8, door[0] - 1.6 + i * 1.1), h.plot[1] - 1.4];
        taken.push([x, z]);
        const fig = at(toyFigure(v.look as any), x, 0, z, Math.PI);
        fig.userData.visitor = v;
        finishShadows(fig);
        people.add(fig);
        if (!fronts[v.id]) { const e = keep(el('div', 'bubble visitor')); e.dataset.who = v.name; ui.appendChild(e); fronts[v.id] = { head: new THREE.Vector3(), el: e, text: '', until: 0, spot: [x, z] }; }
        fronts[v.id].head.set(x, 2.05, z);
        fronts[v.id].spot = [x, z];
      });
      renderer.shadowMap.needsUpdate = true;
    };
    if ((h as any).live) placePeople(((h as any).live as Live).visitors || []);
    walkPeople = () => people.children;
    walkHello = (l: Life) => fx.emoji(top(l.head), '👋', 0.4, 1.8);

    const hearChat = (live: Live) => {
      placePeople(live.visitors || []);
      for (const c of live.chat || []) {
        if (c.at <= chatSeen) continue;
        chatSeen = c.at;
        const f = fronts[c.visitor];
        // (P70) walking, your own words go over the walker, and the plazmi comes over to you there
        const wk = walkerNow();
        if (f && wk && isMe({ id: c.visitor })) { sayOverMe(c.text); L.greet([wk.x, wk.z]); continue; }
        if (f) { f.text = c.text; f.until = performance.now() + 9000; L.greet(f.spot); }
      }
    };
    // your own figure: left out while you walk (you're the walker), and tagged "(you)" while you don't
    // (in the scene, in depth with everyone's names: nametags.ts)
    let youFig: THREE.Object3D | null = null;
    const youTag = new NameTag(null, (out) => (youFig ? youFig.getWorldPosition(out) : null), { text: '(you)', look: 'you' });
    offs.push(() => youTag.remove());
    const pinPeople = () => {
      const t = performance.now(), walking = !!walkerNow();
      let you: THREE.Object3D | null = null;
      for (const fig of people.children) {
        // (and never in your own way: walk.ts leaves out what's walkSkip)
        const m = isMe(fig.userData.visitor), vis = !(m && walking);
        fig.userData.walkSkip = m;
        if (fig.visible !== vis) { fig.visible = vis; renderer.shadowMap.needsUpdate = true; }
        if (m) you = fig;
      }
      youFig = walking ? null : you;
      youTag.owner = youFig;
      for (const [id, f] of Object.entries(fronts)) {
        const on = t < f.until && !(walking && isMe({ id }));
        f.el.style.display = on ? 'block' : 'none';
        if (!on) continue;
        if (f.el.textContent !== f.text) f.el.textContent = f.text;
        const p = f.head.clone().project(cam);
        const x = ((p.x + 1) / 2) * env.vw, y = ((1 - p.y) / 2) * env.vh;
        f.el.style.left = `${x}px`;
        f.el.style.top = `${y}px`;
        pinned.push({ el: f.el, x, y });
      }
    };

    // ----- Lisp objects: a lamp that shows their state, and their words in a bubble
    const objectSaid: Record<string, { el: HTMLElement; head: THREE.Vector3; until: number; at: number }> = {};
    const hearObjects = (live: Live) => {
      for (const o of ((live as any).objects || []) as { id: string; name: string; state: Record<string, any>; said?: { text: string; at: number } }[]) {
        let node: THREE.Object3D | null = null;
        for (const p of pickables) if (p.userData.thing?.id === o.id) node = p;
        if (!node) continue;
        node.traverse((m: any) => { if (m.userData.lamp) m.material = Fu.isOn(o.state) ? Fu.LAMP_ON : Fu.LAMP_OFF; });
        node.userData.thing.state = o.state;
        if (o.said && o.said.at > (objectSaid[o.id]?.at || 0) && live.now - o.said.at < 15000) {
          let b = objectSaid[o.id];
          if (!b) { b = objectSaid[o.id] = { el: keep(el('div', 'bubble object')), head: new THREE.Vector3(), until: 0, at: 0 }; ui.appendChild(b.el); }
          b.el.dataset.who = o.name; b.el.textContent = o.said.text;
          b.at = o.said.at;
          b.until = performance.now() + 8000;
          node.getWorldPosition(b.head);
          b.head.y += node.userData.speaks || 1.2;
        } else if (o.said && !objectSaid[o.id]) objectSaid[o.id] = { el: keep(el('div', 'bubble object')), head: new THREE.Vector3(), until: 0, at: o.said.at };
      }
    };
    const pinObjects = () => {
      const t = performance.now();
      for (const b of Object.values(objectSaid)) {
        const on = t < b.until;
        b.el.style.display = on ? 'block' : 'none';
        if (!on) continue;
        const p = b.head.clone().project(cam);
        const x = ((p.x + 1) / 2) * env.vw, y = ((1 - p.y) / 2) * env.vh;
        b.el.style.left = `${x}px`;
        b.el.style.top = `${y}px`;
        pinned.push({ el: b.el, x, y });
      }
    };
    if ((h as any).live) hearObjects((h as any).live as Live);

    // ----- what the agent does, shown: a move, and something that comes out of the plazmi
    Object.assign(P.scn, { fx, life: L, queue: () => queue.length, guests: () => [...guests.values()].map((e) => e.life), lawn: () => lawn });
    // (P56) `on`: the thing it's done to, where the sparkles (the drops, the confetti) come out;
    // the emoji is always over its own head
    const effect = (act: Act, l: Life, quiet = false, on?: THREE.Vector3) => {
      const at = l.head, floor = l.floorY, e = act.effect, burst = on || at;
      if (!quiet) hearAct(act, l); // (P36: an act done as actions plays its own sounds on their beats)
      if (e.kind === 'coins') { fx.coins(at, e.n, floor); fx.sparkles(top(at), '#ffd36b', 12, 0.4); }
      else if (e.kind === 'notes') fx.notes(at, 4);
      else if (e.kind === 'sparkles') { fx.sparkles(burst, e.color); fx.emoji(top(at), act.emoji); }
      else if (e.kind === 'confetti') { fx.confetti(on ? on.clone().setY(on.y + 0.3) : top(at), on ? 24 : 46, floor); fx.emoji(top(at), act.emoji); }
      else if (e.kind === 'drops') { fx.drops(burst, e.color, 14, floor); fx.emoji(top(at), act.emoji); }
      else if (e.kind === 'emoji') fx.emoji(top(at), e.text);
      else if (e.kind === 'hearts') { fx.hearts(at, 4); fx.emoji(top(at), act.emoji); }
      else if (e.kind === 'lantern') { fx.lanterns(top(at), 1, 0.05); fx.sparkles(top(at), '#ffcf7a', 10, 0.35); fx.emoji(top(at), act.emoji); }
      else if (e.kind === 'handshake') { fx.sparkles(top(at), '#ffd36b', 16, 0.45); fx.emoji(top(at), act.emoji, 0.5); }
      else fx.emoji(top(at), act.emoji, 0.45);
    };

    // ----- neighbourly coins as they're paid (warmth), and festivals and things built together
    // as they happen: coins spring out of whoever earned them; a festival gets its own celebration
    const paidSeen = new Map<string, number>();
    const coinsFor = (who: string, paid: Paid[] | undefined, l: Life | null, now: number) => {
      const fresh = (paid || []).filter((p) => p.at > (paidSeen.get(who) ?? (now - 90000)));
      if (!fresh.length) return;
      paidSeen.set(who, Math.max(...fresh.map((p) => p.at)));
      const n = fresh.reduce((s, p) => s + p.coins, 0);
      // away from home, the coins come to the door
      const at = l && l.root.visible ? l.head : new THREE.Vector3(door[0], 1.4, door[1] + 0.6);
      fx.coins(at, Math.min(16, 3 + n * 2), l && l.root.visible ? l.floorY : 0);
      fx.sparkles(top(at), '#ffd36b', 10, 0.4);
      fx.label(top(at).add(new THREE.Vector3(0, 0.25, 0)), `+${n}`);
    };
    const celebrated = new Set<string>();
    const roofTop = () => { if (!shellNow) return 6; const b = new THREE.Box3().setFromObject(shellNow); return Math.min(16, b.max.y); };
    const celebrate = (n: News) => {
      if (celebrated.has(n.id)) return;
      celebrated.add(n.id);
      const ground = new THREE.Vector3(lawn.c[0], 0.2, lawn.c[1]);
      const sky = roofTop();
      const site = n.kind === 'project' && n.here ? ((h as any).village || []).find((p: any) => p.id === n.id && p.role === 'site')?.at : null;
      // fireworks go up from behind the house and bloom either side of its roof, where the sky shows
      const sides = (count: number) => {
        for (let i = 0; i < count; i++) {
          const side = i % 2 ? 1 : -1, x = W / 2 + side * (0.42 + Math.random() * 0.3) * W;
          fx.fireworks(new THREE.Vector3(x, sky * 0.4, D * (0.15 + Math.random() * 0.35)), 1, 0.6, sky * (0.8 + Math.random() * 0.3), i * (0.45 + Math.random() * 0.4));
        }
      };
      if (n.kind === 'project') {
        const at = site ? new THREE.Vector3(site[0], 0.2, site[1]) : ground;
        fx.fireworks(at, 3, 1.5, Math.max(4.5, sky * 0.8));
        fx.confetti(at.clone().setY(2.2), 60, 0);
        fx.sparkles(at.clone().setY(1.2), '#8fd1c0', 18, 0.9);
        fx.emoji(at.clone().setY(2.6), '🎉', 0.6, 3);
      } else if (n.what === 'lantern-evening') {
        fx.lanterns(new THREE.Vector3(door[0], 1.2, door[1] + 1.4), 10, 2.2);
        fx.emoji(new THREE.Vector3(door[0], 2.6, door[1] + 1.2), '🏮', 0.6, 3);
      } else if (n.what === 'garden-day') {
        fx.petals(new THREE.Vector3(lawn.c[0], 0, lawn.c[1] - 1), Math.max(W, D) * 0.42, 150);
        fx.sparkles(ground.clone().setY(1.0), '#f6c3d0', 20, 1.6);
        fx.emoji(ground.clone().setY(2.4), '🌷', 0.6, 3);
      } else {
        // a Dance Night, a housewarming: fireworks over the house, and confetti on the lawn
        sides(n.what === 'dance-night' ? 8 : 5);
        fx.confetti(ground.clone().setY(2.4), 50, 0);
        fx.emoji(ground.clone().setY(2.6), n.what === 'housewarming' ? '🏡' : '💃', 0.6, 3);
        if (n.what === 'housewarming') fx.hearts(ground.clone().setY(1.6), 5);
      }
    };
    // for staged scenes: a celebration on demand
    P.scn.celebrate = (n: News) => { celebrated.delete(n.id); celebrate(n); };
    P.scn.machines = M; P.scn.dinner = Dn;
    const hearNews = (live: Live) => {
      const l = live as any;
      coinsFor(slug, l.paid, L, live.now);
      for (const g of (live.guests || []) as (Guest & { paid?: Paid[] })[]) coinsFor(g.slug, g.paid, guests.get(g.slug)?.life || null, live.now);
      for (const n of (l.news || []) as News[]) if (live.now - n.at < 30 * 60000) celebrate(n);
    };

    // songs: notes rise from whoever's singing, for a while, and it sways to the tune
    const singing = new Map<Life, number>();
    const sing = (l: Life, text: string) => {
      l.bubble(`♪ ${text} ♪`, 8); singing.set(l, performance.now() + 7000);
      if (!l.dancing) l.perform('sing', 'singing');
    };
    let notesAt = 0;

    // ----- neighbours over: their own plazmi, in their own colours, on this lawn
    type GuestView = { life: Life; g: Guest; bubble: HTMLElement; tag: NameTag; emoteSeen: number; saidSeen: number; leaving: boolean };
    const guests = new Map<string, GuestView>();
    offs.push(() => { for (const e of guests.values()) e.tag.remove(); });
    soundLives(() => [L, ...[...guests.values()].map((e) => e.life)]);
    // everyone here: the resident (when home) and the neighbours over, by slug
    const present = () => [...(L.root.visible ? [{ slug, l: L, name: h.resident.name }] : []),
      ...[...guests.entries()].filter(([, e]) => !e.leaving).map(([s, e]) => ({ slug: s, l: e.life, name: e.g.name }))];
    const partnerOf = (s: string) => present().find((p) => p.slug === s)?.l || null;
    find = (s: string) => { const l = partnerOf(s); return l && l.root.visible ? l.head.clone() : null; };
    walkLives = () => [L, ...[...guests.values()].filter((e) => !e.leaving).map((e) => e.life)];
    const names = (ns: string[]) => (ns.length < 2 ? ns.join('') : `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`);
    // the same dance, all together, from the next bar after `start`: the leader in
    // the middle, the resident beside it, the guests either side, in slug order (so
    // every visitor's page lines them up the same)
    const danceTogether = (leader: string, beats: Beat[], start: number, dance?: string) => {
      const who = present().sort((a, b) => (a.slug === leader ? -1 : b.slug === leader ? 1 : a.slug === slug ? -1 : b.slug === slug ? 1 : a.slug.localeCompare(b.slug)));
      if (!who.length) return;
      const others = who.filter((w) => w.slug !== slug).map((w) => w.name);
      const label = `dancing${dance ? ` ${dance.replace(/-/g, ' ')}` : ''}${others.length ? ` with ${names(others)}` : ''}`;
      const tr = makeTroupe(beats, start, lawn.c, audience, who.length, label, lawn.r);
      if (dance) tr.title = danceTitle({ dance });
      who.forEach((w, i) => w.l.joinTroupe(tr, i));
    };
    // two plazmi meeting say hello with a little dance; a crowd makes it a ring
    const greet = (arrived: Guest) => {
      const n = present().length;
      if (n < 2) return;
      const beats: Beat[] = n === 2 ? [{ move: 'wave' }, { move: 'hop' }, { move: 'boop' }, { move: 'heart' }]
        : [{ move: 'wave' }, { move: 'dance' }, { move: 'spin-hop' }, { move: 'heart' }];
      danceTogether(arrived.slug, beats, nextBar(arrived.since, 7000));
    };
    const play = (l: Life, em: Emote, who: string) => {
      let beats: Beat[] = em.beats?.length ? em.beats : [{ move: em.move || 'bob' }];
      // shown to someone (a dance taught): every step faces them
      if (em.with && !beats.some((b) => b.with || b.at)) beats = beats.map((b) => ({ ...b, with: em.with }));
      const together = Array.isArray(em.group) || em.move === 'dance';
      if (together) { danceTogether(who, beats, nextBar(em.at, 6500), em.dance); fx.notes(l.head, 3); }
      else if (beats.length === 1 && beats[0].move === 'sing' && !beats[0].at && !beats[0].with) { sing(l, em.text); return; }
      // (P84) the words show when the move does: a plazmi indoors steps out where it's seen
      // first, and its "*waves.*" isn't left over an empty porch meanwhile
      else { l.choreo(beats, `*${em.text}`, partnerOf, () => { fx.emoji(top(l.head), moveEmoji(beats[0].move)); l.bubble(`*${em.text}*`, 6); }); return; }
      l.bubble(`*${em.text}*`, 6);
    };
    let tagsSmall = false;
    const hearGuests = (live: Live) => {
      const list = live.guests || [];
      for (const g of list) {
        let e = guests.get(g.slug);
        if (!e) {
          const gl = ready.get(g.slug) || new Life(guestHouse(g), markers, { inside: false, guest: true });
          ready.delete(g.slug);
          gl.audience = audience; gl.emit = fxEmit; gl.lawn = lawn; gl.eye = cam;
          if (site) gl.extra = site.onChore; // a neighbour come to help build hammers at the site
          // its outfits for every mood a dance needs, built while the page is idle
          const warmG = () => { if (!dead && gl.prewarmNext(renderer, cam)) idle(warmG); };
          idle(warmG);
          root.add(gl.root);
          const bubble = keep(el('div', 'bubble')); bubble.style.display = 'none'; bubble.dataset.who = g.name; ui.appendChild(bubble);
          const tag = new NameTag(gl.root, (out) => { out.copy(gl.head); out.y -= 1.55; return out; }, { text: `${g.name} ${g.emoji || ''}`.trim(), small: tagsSmall });
          const fresh = live.now - g.since < 90000;
          e = { life: gl, g, bubble, tag, emoteSeen: fresh ? 0 : g.emote?.at || 0, saidSeen: fresh ? 0 : g.said_at || 0, leaving: false };
          guests.set(g.slug, e);
          gl.setLive({ now: live.now, version: live.version, chores: live.chores, plan: [] } as Live);
          // plazmi say hello by dancing; a neighbour come to help gets straight to it, with a hand held up
          if (g.why !== 'help') { gl.arrive(); if (fresh) greet(g); }
          else if (fresh) setTimeout(() => { if (!dead) fx.emoji(top(gl.head), '🤝', 0.5, 2.2); }, 900);
        }
        e.g = g;
        e.life.recolour(g.color, g.glow);
        // its name tag says what it's lending a hand with
        const tagText = `${g.name} ${g.emoji || ''}`.trim() + (g.why === 'help' && g.chore ? ` · ${DOING[g.chore] || g.chore}` : '');
        e.tag.set({ text: tagText });
        e.life.setLive({ now: live.now, version: live.version, chores: live.chores, plan: g.chore ? [{ chore: g.chore, start: g.start!, ends: g.ends! }] : [] } as Live);
        if (g.emote && g.emote.at > e.emoteSeen) { e.emoteSeen = g.emote.at; if (live.now - g.emote.at < 60000) play(e.life, g.emote, g.slug); }
        if (g.said && g.said_at && g.said_at > e.saidSeen) { e.saidSeen = g.said_at; if (live.now - g.said_at < 120000) { if (g.sung) sing(e.life, g.said); else e.life.bubble(g.said, 8); } }
      }
      for (const [s, e] of guests) if (!e.leaving && !list.some((g) => g.slug === s)) { e.leaving = true; e.life.leave(); }
    };
    const pinGuests = (dt: number) => {
      for (const [s, e] of guests) {
        e.life.update(dt);
        if (e.life.gone) { root.remove(e.life.root); e.bubble.remove(); e.tag.remove(); guests.delete(s); continue; }
        const sp = e.life.speech;
        const p = e.life.head.clone().project(cam);
        const x = ((p.x + 1) / 2) * env.vw, y = ((1 - p.y) / 2) * env.vh;
        e.bubble.style.display = sp ? 'block' : 'none';
        if (sp) { if (e.bubble.textContent !== sp.text) e.bubble.textContent = sp.text; e.bubble.style.left = `${x}px`; e.bubble.style.top = `${y}px`; pinned.push({ el: e.bubble, x, y }); }
      }
      // in a crowd the tags get a little smaller
      const crowd = guests.size >= 4;
      if (crowd !== tagsSmall) {
        tagsSmall = crowd;
        for (const e of guests.values()) e.tag.set({ small: crowd });
      }
    };

    // its own emotes and songs, and where it is (over at a neighbour's, it isn't home)
    const live0: Live | null = (h as any).live || null;
    let emoteSeen = live0?.emote && live0.now - live0.emote.at > 20000 ? live0.emote.at : 0;
    let sungSeen = live0?.said_at && live0.now - live0.said_at > 20000 ? live0.said_at : 0;
    let away: { house: string; name: string } | null = null;
    // (P68) away: a way to go and watch ("Watch" while it gathers in the wilds). Outside, a pill beside Next door,
    // so Look inside stays where it always is (climb round 4); inside, the top bar's primary as before
    let watchOff: (() => void) | null = null, watchKey = '';
    offs.push(() => { watchOff?.(); watchOff = null; });
    const setWatch = (o: any) => {
      const key = o && !following() ? `${o.why}:${o.house}` : '';
      if (key === watchKey) return;
      watchKey = key; watchOff?.(); watchOff = null;
      if (!key) { awayPill(null); return; }
      const label = o.why === 'gather' ? `Watch ${h.resident.name}` : isPhone() ? `Follow to ${o.name}` : `Follow ${h.resident.name} to ${o.name}`;
      if (q.get('view') === 'inside') watchOff = shell().over({
        label: o.why === 'gather' ? 'Watch' : `Follow ${h.resident.name}`, short: o.why === 'gather' ? 'Watch' : 'Follow',
        title: `Go and watch ${h.resident.name} at ${o.name}`, onClick: () => startFollow(slug),
      });
      else { awayPill({ label, onClick: () => startFollow(slug) }); watchOff = () => awayPill(null); }
    };
    const hearSelf = (live: Live) => {
      away = live.out || null;
      L.away = !!away;
      setWatch(away);
      // (P56) an emote about a thing here (`weed aster-bed`) is played in its turn from the feed, at the thing
      const atThing = (at: number) => (((live as any).feed || []) as FeedLine[]).some((e) => Math.abs(e.at - at) < 400 && resolve(e, null).things.length > 0);
      if (live.emote && live.emote.at > emoteSeen) { emoteSeen = live.emote.at; if (live.now - live.emote.at < 60000 && !away && !atThing(live.emote.at)) play(L, live.emote, slug); }
      if (live.sung && live.said && live.said_at && live.said_at > sungSeen) { sungSeen = live.said_at; if (live.now - live.said_at < 60000) sing(L, live.said); }
    };

    // ----- (P56) a command about a thing is done at the thing. `place lamp-post 10.5 17.5`:
    // out of the door with the bundle, set down there, the lamp post popping in as it lands.
    // `fill birdbath`: over to the birdbath, turned to it. The thing is found in the house by
    // the server's `acted` (its id), by where it was put, or by name.
    type Placing = { x: number; z: number; level: number; at: number; state: 'waiting' | 'popping' | 'done'; pop: number; obj: THREE.Object3D | null };
    const placings: Placing[] = [];
    const PLANTS = /^(garden\/|plants\/)/, TREES = /^(oak|cherry|apple|birch|pine)$/;
    const isPlant = (t: Fu.Thing) => PLANTS.test(t.item || '') && !/bench|birdbath|lamp|mailbox|bins|fence|sundial|gnome|path|arch|pond|shed|bird/.test(t.item || '');
    const allThings = () => [...h.furniture, ...h.yard];
    const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '-').replace(/^(the|a|an|my)-/, '');
    const byName = (name: string): Fu.Thing[] => {
      const n = norm(name), all = allThings();
      const exact = all.filter((t) => t.id === n);
      if (exact.length) return exact;
      const named = all.filter((t) => [t.render, (t.item || '').split('/')[1], t.name && norm(t.name), (t as any).nick && norm((t as any).nick)].some((k) => k && (k === n || k === n.replace(/s$/, ''))));
      if (named.length) return named;
      // `water plants`, `weed the garden`: the plants in the garden, a few of them
      if (/^(plants?|flowers?|garden|beds?|the-garden)$/.test(n)) {
        const [px, pz] = L.spot, d = (t: Fu.Thing) => Math.hypot(t.at[0] - px, t.at[1] - pz);
        return h.yard.filter((t) => isPlant(t) && !TREES.test(t.render || '')).sort((a, b) => d(a) - d(b)).slice(0, 3);
      }
      return [];
    };
    const resolve = (e: FeedLine, acted: Live['acted'] | undefined): { things: Fu.Thing[]; aim: Aim | null } => {
      const aim = aimOf(e.command);
      if (acted?.object) { const t = allThings().find((x) => x.id === acted.object); if (t) return { things: [t], aim }; }
      if (!aim) return { things: [], aim };
      if (aim.placing && aim.at) {
        const [x, z] = aim.at, L0 = aim.level ?? 0;
        const t = allThings().find((q) => Math.abs(q.at[0] - x) < 0.3 && Math.abs(q.at[1] - z) < 0.3 && (q.level || 0) === L0 && (norm(q.id).startsWith(aim.name) || q.render === aim.name));
        // not in the house in view yet (it comes with the next rebuild): where it was put
        return { things: [t || { id: `placing:${aim.name}`, render: aim.name, item: `garden/${aim.name}`, at: [x, z], level: L0 } as Fu.Thing], aim };
      }
      const ts = byName(aim.name);
      // more than one of a name (two lamp posts): the one nearest where it stands
      if (ts.length > 1 && !/^(plants?|flowers?|garden|beds?)$/.test(aim.name)) {
        const [px, pz] = L.spot;
        ts.sort((a, b) => Math.hypot(a.at[0] - px, a.at[1] - pz) - Math.hypot(b.at[0] - px, b.at[1] - pz));
        return { things: ts.slice(0, 1), aim };
      }
      return { things: ts, aim };
    };
    const thingPos = (t: Fu.Thing, y = 0.35) => new THREE.Vector3(t.at[0], L.floorOf(t.at, t.level || 0) + y, t.at[1]);
    const near = (p: Placing, t: Fu.Thing) => Math.abs(p.x - t.at[0]) < 0.3 && Math.abs(p.z - t.at[1]) < 0.3 && p.level === (t.level || 0);
    // a thing being placed stays hidden until it's set down; then it pops in
    const objFor = (p: Placing) => { for (const o of pickables) { const t = o.userData.thing as Fu.Thing | undefined; if (t && t.at && near(p, t)) return o; } return null; };
    const hidePlacings = () => {
      const now = performance.now();
      for (let i = placings.length - 1; i >= 0; i--) if (now - placings[i].at > 150000) placings.splice(i, 1);
      for (const p of placings) {
        if (p.state === 'done') continue;
        p.obj = objFor(p);
        if (!p.obj) continue;
        if (p.state === 'waiting') p.obj.visible = false;
        else { p.obj.scale.setScalar(0.001); }
      }
      renderer.shadowMap.needsUpdate = true;
    };
    const setDown = (t: Fu.Thing) => {
      const p = placings.find((q) => q.state === 'waiting' && near(q, t));
      const at = thingPos(t, 0.15);
      fx.sparkles(at.clone().setY(at.y + 0.25), isPlant(t) ? '#b5e08a' : '#ffe08a', 16, 0.55);
      fx.drops(at, isPlant(t) ? '#8a6a4a' : '#d8cdb8', 8, at.y - 0.15);
      if (!p) return;
      p.state = 'popping'; p.pop = 0; p.obj = objFor(p);
      if (p.obj) { p.obj.visible = true; p.obj.scale.setScalar(0.001); }
    };
    const tickPlacings = (dt: number) => {
      let moved = false;
      const now = performance.now();
      for (const p of placings) {
        // never left hidden: an act that didn't play (a chore under way, the page away) shows it anyway
        if (p.state === 'waiting' && (now - p.at > 45000 || (!queue.length && !L.acting && now - p.at > 8000))) { p.state = 'popping'; p.pop = 0; p.obj = objFor(p); }
        if (p.state !== 'popping') continue;
        p.pop = Math.min(1, p.pop + dt / 0.45);
        // overshoot a little and settle, like something set down with a bounce
        const k = p.pop, s = k < 1 ? 1 + 1.7 * Math.pow(k - 1, 3) + 0.7 * Math.pow(k - 1, 2) : 1;
        if (!p.obj || !p.obj.parent) p.obj = objFor(p);
        if (p.obj) { p.obj.visible = true; p.obj.scale.setScalar(Math.max(0.001, s)); moved = true; }
        if (k >= 1) p.state = 'done';
      }
      if (moved) renderer.shadowMap.needsUpdate = true;
    };
    { const lotWas = afterLot; afterLot = (house) => { lotWas(house); hidePlacings(); }; }

    // the camera keeps it in view: when an act takes it out of the frame (out into the garden
    // from the cutaway, round the back of the house), the view drifts after it until it's back in
    let follow = 0, chasing = false, goFloor: ((k: number) => void) | null = null;
    const NDC = new THREE.Vector3(), AIM = new THREE.Vector3();
    const keepInView = (dt: number) => {
      if (follow <= 0 || P.scn.walking?.() || !L.root.visible) { chasing = false; return; }
      follow -= dt;
      // where it is on the screen (the panels sit on the right: a little left of the middle is the middle)
      NDC.copy(L.head).setY(L.head.y - 0.5).project(cam);
      const x = NDC.x + 0.15, off = Math.max(Math.abs(x) - 0.6, Math.abs(NDC.y) - 0.6, NDC.z > 1 ? 1 : 0);
      if (off > 0) chasing = true;
      else if (Math.abs(x) < 0.2 && Math.abs(NDC.y) < 0.25) chasing = false;
      if (!chasing) return;
      // the point under it, on the plane the camera looks at, slid toward gently
      AIM.set(L.head.x, controls.target.y, L.head.z);
      const d = AIM.sub(controls.target).multiplyScalar(Math.min(1, dt * 1.6));
      controls.target.add(d); cam.position.add(d);
    };

    const playAt = (e: FeedLine, act: Act, acted: Live['acted'] | undefined) => {
      const { things, aim } = resolve(e, acted);
      if (!things.length) return false;
      const t0 = things[0], plant = aim?.placing ? isPlant(t0) || /bed|bush|flower|rose|tree|pine|oak|birch|cherry|apple|hydrangea|aster|sunflower|lavender|hedge|fern|tulip|daisy|herb|veg/.test(aim.name) : isPlant(t0);
      const outdoors = h.yard.includes(t0) || (aim?.placing && !h.furniture.includes(t0) && !aim.level && !L.indoorsAt(t0.at));
      const name = (t0.name || t0.render || aim?.name || 'thing').replace(/-/g, ' ').toLowerCase();
      const verb = acted?.verb || aim?.verb || '';
      // the HUD says what's seen: a verb with only a general word in acts.ts says what and where
      const several = things.length > 1 ? (aim?.name || 'plants').replace(/-/g, ' ') : name;
      const ON: Record<string, string> = { sit: 'sitting on', nap: 'napping on', sleep: 'sleeping in', lie: 'lying on', look: 'looking at', l: 'looking at', x: 'looking at', examine: 'looking at', admire: 'admiring' };
      let label = act.kind === 'chore' || act.emoji === '✨' || ON[verb] ? `${ON[verb] || ing(verb || 'tend')} the ${several}` : act.words;
      let way = things.length > 1 ? `going round the ${several}` : `on the way to the ${name}`;
      let o: Parameters<Life['actAt']>[2] = {};
      if (aim?.placing) {
        label = plant ? `planting the ${name}` : act.words;
        way = plant ? `taking the ${name} out to plant` : `carrying the ${name} ${outdoors ? 'out ' : ''}to its spot`;
        o = { carry: plant ? 'basket' : 'bundle', seq: plant ? [['plant', 3.4]] : [['unpack', 2.8]], made: setDown };
      } else if (['sit', 'nap', 'sleep', 'lie'].includes(verb)) {
        o = { sit: true, sitSecs: verb === 'sit' ? 25 : 45, arrive: () => effect(act, L) };
      } else if (verb === 'watch' && t0.render === 'tv') {
        // `watch tv`: settled on the nearest seat in the room, if there is one (the screen lights: Fu.watchTv)
        const d = (s: Fu.Thing) => Math.hypot(s.at[0] - t0.at[0], s.at[1] - t0.at[1]);
        const seat = h.furniture.filter((s) => ['sofa', 'armchair', 'chair'].includes(s.render || '') && (s.level || 0) === (t0.level || 0) && s.room === t0.room).sort((a, b) => d(a) - d(b))[0];
        label = 'watching the television';
        if (seat) { things.splice(0, things.length, seat); way = `settling in to watch the television`; o = { sit: true, sitSecs: 120 }; }
        else o = { move: 'look' };
      } else {
        const w = VERB_WORK[verb] || MORE_WORK[verb] || workFor(act, e.command)?.[0]?.[0] || null;
        const spark = () => { const at = thingPos(t0, 0.6); fx.sparkles(at, act.effect.kind === 'sparkles' && act.effect.color ? act.effect.color : '#ffe08a', 10, 0.4); };
        if (w) o = { seq: [[w, plantTime(w)]], arrive: () => hearAct(act, L), made: spark };
        else o = { move: act.move === 'look' ? 'look' : 'nod', arrive: () => { effect(act, L, false, thingPos(t0, 0.6)); } };
        if (verb === 'light') o.made = () => { const at = thingPos(t0, 1.2); fx.sparkles(at, '#ffcf7a', 14, 0.35); };
      }
      // a long queue: quicker on its feet, so each act is still seen where it happens
      L.hurry = queue.length >= 4 ? 2 : queue.length >= 2 ? 1.6 : 1.25;
      if (!L.actAt(things, label, { ...o, way })) return false;
      follow = 30;
      // in the cutaway, a floor other than the thing's: that floor (the garden is the ground floor's)
      if (inside && goFloor) goFloor(outdoors ? 0 : t0.level || 0);
      return true;
    };
    // garden verbs with an action of their own, beyond VERB_WORK's
    const MORE_WORK: Record<string, string> = { fill: 'water', mist: 'water', weed: 'pick', prune: 'pick', deadhead: 'pick', trim: 'pick', compost: 'compost', clean: 'dust', polish: 'dust', wipe: 'dust' };
    const plantTime = (w: string) => (w === 'plant' ? 3.4 : w === 'water' ? 3.6 : w === 'pick' ? 2.6 : w === 'rake' ? 2.6 : 3);

    let talkRefresh = () => {};
    // every command in the feed plays out, one after another
    type FeedLine = { at: number; command: string; reply: string; ok: boolean; who?: string; guest?: string; n?: number };
    let feedSeen = Math.max(0, ...((((h as any).live?.feed) || []) as FeedLine[]).map((e) => e.at));
    const queue: { e: FeedLine; act: Act; acted?: Live['acted'] }[] = [];
    let nextAct = 0, actFrom = 0;
    // (P56) the server's `acted` and `emote` are the latest only: they belong to the one line
    // of the feed sent with them (the same moment, give or take), not to a whole burst
    const sameMoment = (a: number, b: number) => Math.abs(a - b) < 400;
    L.feedActs = true;
    const hearFeed = (live: Live) => {
      for (const e of (((live as any).feed) || []) as FeedLine[]) if (e.at > feedSeen) {
        feedSeen = e.at;
        if (e.guest) continue;
        const acted = live.acted && sameMoment(live.acted.at, e.at) ? live.acted : null;
        // the page plays this one, in its turn, rather than Life straight away
        if (acted) L.seenActed(acted.at);
        const act = actFor(e.command, e.reply, e.ok);
        queue.push({ e, act, acted });
        const aim = e.ok ? aimOf(e.command) : null;
        if (aim?.placing && aim.at) placings.push({ x: aim.at[0], z: aim.at[1], level: aim.level ?? 0, at: performance.now(), state: 'waiting', pop: 0, obj: null });
      }
    };
    const playFeed = (t: number) => {
      if (!queue.length || t < nextAct) return;
      // (P56) an act at a thing is seen through, where it happens, before the next (at most 30 s)
      if (L.acting && t - actFrom < 30000) return;
      const { e, act, acted } = queue.shift()!;
      nextAct = t + 2300;
      // (P56) a command about a thing: walked to, and done there (from another floor of the
      // cutaway too: it comes up the stairs to it)
      if (!away && e.ok !== false && playAt(e, act, acted)) { actFrom = t; nextAct = t + 600; return; }
      if (!L.root.visible) return;
      // an emote plays itself out (see play); the server already moves it for a verb on a thing or a song
      const emoted = !!latest?.emote && sameMoment(latest.emote.at, e.at);
      if (emoted) return;
      // (P36) a command with actions of its own (cooking, writing, Lisp…): done where it can be seen, with their sounds
      const onThing = !!acted, seq = onThing ? null : workFor(act, e.command);
      if (seq && L.workSeq(seq, act.words)) { effect(act, L, true); return; }
      const served = onThing || (act.kind === 'play' && act.move !== 'hobby') || act.kind === 'social' || act.move === 'sing';
      if (served) effect(act, L);
      else if (act.move === 'walk') { L.goRoom(act.room || '', act.words); effect(act, L); }
      // anything else is a move out where it can be seen, and the coins (or the sparkles) come as it starts
      else L.perform(act.move, act.words, undefined, () => effect(act, L));
    };

    const hud = makeHud(h);
    for (const e of hud.els) panel(e);
    let latest: Live | null = (h as any).live || null;
    if (latest) { hud.update(latest); hearGuests(latest); hearSelf(latest); hearNews(latest); }
    for (const gl of ready.values()) root.remove(gl.root);
    ready.clear();

    // ----- a closer look: hover shows a ring, a click opens a card
    const inspector = makeInspector(() => h, () => latest, L, cam, env);
    keep(inspector.card);
    root.add(inspector.ring);
    const pick = (cx: number, cy: number) => inspector.pick(cx, cy, [...pickables, ...people.children, L.root, ...M.pickables]);
    let down: { x: number; y: number; t: number } | null = null;
    let hoverAt: { x: number; y: number } | null = null;
    const canvas = renderer.domElement;
    listen(canvas, 'pointerdown', (e: PointerEvent) => { down = { x: e.clientX, y: e.clientY, t: performance.now() }; });
    listen(canvas, 'pointerup', (e: PointerEvent) => {
      if (P.scn.walking?.()) { down = null; return; }
      if (down && Math.hypot(e.clientX - down.x, e.clientY - down.y) < 6 && performance.now() - down.t < 600) inspector.open(pick(e.clientX, e.clientY), e.clientX, e.clientY);
      down = null;
    });
    listen(canvas, 'pointermove', (e: PointerEvent) => { if (!down) hoverAt = { x: e.clientX, y: e.clientY }; });
    listen(window, 'keydown', (e: KeyboardEvent) => { if (e.key === 'Escape') inspector.close(); });
    // for staged scenes: open the card for a thing by its id, where it stands on screen
    P.scn.inspect = (id: string) => {
      let found: THREE.Object3D | null = null;
      for (const p of pickables) p.traverse((o) => { if (!found && o.userData.thing?.id === id) found = o; });
      if (!found) return false;
      const c = new THREE.Box3().setFromObject(found).getCenter(new THREE.Vector3()).project(cam);
      inspector.open(found, ((c.x + 1) / 2) * env.vw, ((1 - c.y) / 2) * env.vh);
      return true;
    };
    // for staged scenes: where a thing is (its box's middle), by its id
    P.scn.where = (id: string) => {
      let found: THREE.Object3D | null = null;
      for (const p of pickables) p.traverse((o) => { if (!found && o.userData.thing?.id === id) found = o; });
      return found ? new THREE.Box3().setFromObject(found).getCenter(new THREE.Vector3()).toArray() : null;
    };

    // ----- floors: on a house with storeys or a basement, the inside view goes one floor at a
    // time. The tabs read top to bottom, with the lawn between the ground floor and the basements.
    if (inside && levels.length > 1) {
      const NAMES: Record<number, string> = { 3: 'Lookout', 2: 'Loft', 1: 'Upstairs', [-1]: 'Basement', [-2]: 'Lower basement' };
      const label = (k: number) => (k === 0 ? (levels.length === 2 && levels[0] === 0 ? 'Downstairs' : 'Ground floor') : NAMES[k] || `Floor ${k}`);
      const box = el('div', 'floors');
      const buttons = new Map<number, HTMLElement>();
      const showFloor = (k: number) => {
        const dy = (k - floor) * SH;
        floor = k;
        buildLot(h);
        L.setHouse(h, markers);
        L.viewFloor = k;
        M.viewFloor = k; M.setHouse(h, lot, markers); Dn.setHouse(h, lot);
        controls.target.y += dy; cam.position.y += dy;
        for (const [j, b] of buttons) b.classList.toggle('on', j === k);
        // a new storey or basement stays bare until the resident furnishes it
        const bare = k !== 0 && !h.furniture.some((t) => (t.level || 0) === k);
        if (bare) shell().toast(`Nothing ${k > 0 ? 'up' : 'down'} here yet: it's ${h.resident.name}'s to furnish.`);
        const u = new URL(location.href);
        if (k) u.searchParams.set('floor', String(k)); else u.searchParams.delete('floor');
        history.replaceState(null, '', u);
        inspector.close();
      };
      for (const k of [...levels].reverse()) {
        if (k === -1) box.appendChild(el('div', 'grade'));
        const b = el('button', [k === floor ? 'on' : '', k < 0 ? 'below' : ''].filter(Boolean).join(' '), label(k));
        b.onclick = () => { if (k !== floor) showFloor(k); };
        box.appendChild(b);
        buttons.set(k, b);
      }
      box.classList.add('pz-floors'); box.dataset.pz = 'floors';
      for (const b of box.querySelectorAll('button')) b.classList.add('pz-btn', 'small');
      shell().ctx('left', box);
      panel(box);
      if (floor) { const k = floor; floor = 0; controls.target.y -= k * SH; cam.position.y -= k * SH; showFloor(k); }
      goFloor = (k) => { if (k !== floor && levels.includes(k)) showFloor(k); };
    }

    // ----- each frame: before the camera moves and the scene is drawn, and after
    before = (now, dt) => {
      if (hoverAt && P.scn.walking?.()) hoverAt = null;
      if (hoverAt) { hot = inspector.hover(pick(hoverAt.x, hoverAt.y)); hoverAt = null; }
      L.update(dt);
      M.update(dt);
      site?.update();
      // at the table: the resident (when it's home) and the guests who came for dinner
      const din = (latest as any)?.dinner as DinnerLive | null | undefined;
      if (din?.on) {
        const came = new Set(din.guests.map((g) => g.slug));
        Dn.update(dt, [...(away ? [] : [{ l: L, since: din.at }]),
          ...[...guests.entries()].filter(([s, e]) => came.has(s) && !e.leaving).map(([, e]) => ({ l: e.life, since: Math.max(din.at, e.g.since || 0) }))], L);
      } else Dn.update(dt, [], away ? null : L);
      pinGuests(dt);
      tickPlacings(dt); keepInView(dt);
      playFeed(now);
      if (now > notesAt) {
        notesAt = now + 640;
        const dancers = [L, ...[...guests.values()].map((e) => e.life)].filter((l) => l.root.visible && l.dancing);
        for (const l of dancers) fx.notes(l.head, 1);
        for (const [l, until] of singing) { if (now > until) singing.delete(l); else if (l.root.visible) fx.notes(l.head, 1); }
      }
      inspector.tick(dt);
    };
    after = () => {
      const sp = L.speech;
      pin(L.head, sp && L.root.visible ? sp.text : null, sp?.murmur);
      pinPeople();
      pinObjects();
      declutter();
      hud.tick(L, away, [...guests.values()].filter((e) => !e.leaving).map((e) => e.g));
    };

    // ----- stay live: a small poll; the lot rebuilds only when the house or its chores change
    // (and every few world hours, as the garden grows and the season turns)
    const gardenTick = (now?: number) => Math.floor((now || 0) / (3 * 3600000));
    let lastShape = JSON.stringify([h.version, h.chores, gardenTick((h as any).live?.now)]);
    const poll = async () => {
      try {
        const res = await fetch(`/api/house/${encodeURIComponent(slug)}/live`);
        if (!res.ok || dead) return;
        const live: Live = await res.json();
        if (dead) return;
        latest = live;
        daylight.setNow(live.now);
        if ((live as any).sky) daylight.setFireflies((live as any).sky.fireflies);
        weather?.set(skyOf(live, weatherQ));
        L.setLive(live);
        Fu.watchTv(h, live);
        site?.setLive((live as any).build, live.now);
        L.recolour((live as any).color, (live as any).glow);
        M.setLive(live as any);
        Dn.setLive(live as any);
        hearChat(live);
        hearGuests(live);
        hearSelf(live);
        hearFeed(live);
        hearNews(live);
        talkRefresh();
        hearObjects(live);
        hud.update(live);
        const shape = JSON.stringify([live.version, live.chores, gardenTick(live.now)]);
        // while walking, the walk holds the house as it was (its roof lifted off, floors cut away):
        // a rebuild now would put a fresh, whole house over you. It waits for the next poll after.
        if (shape !== lastShape && !P.scn.walking?.()) {
          const fresh = await fetchHouse(slug, opts.ask);
          if (fresh && !dead) {
            h = fresh; buildLot(fresh); L.setHouse(fresh, markers); lastShape = shape; inspector.close();
            M.setHouse(fresh, lot, markers); Dn.setHouse(fresh, lot);
            lawn = findLawn(fresh); L.lawn = lawn; for (const e of guests.values()) e.life.lawn = lawn;
            P.rebuilt(fresh);
            // a new name (or a new look) shows in the label straight away
            plaque(fresh);
          }
        }
      } catch { /* the next poll will do */ }
    };
    const timer = setInterval(poll, POLL_MS);
    offs.push(() => clearInterval(timer));

    const talk = talkOf();
    talk.bind(slug, () => latest?.now || Date.now());
    talkRefresh = () => talk.refresh();
    said = () => { poll(); talk.refresh(); };
    joined = () => talk.joined(latest?.now || Date.now());
  };

  // the home goes: its scene, words and listeners at once, its panels faded
  const dispose = () => {
    dead = true;
    for (const f of offs) f();
    root.removeFromParent();
    for (const e of doms) e.remove();
    for (const e of panels) {
      // the talk sits in the left column with the hello card: it goes at once, so nothing shifts
      if (e.closest('.leftcol')) e.remove(); else fadeOut(e);
    }
  };

  return {
    slug, root, lot, target, dist, spot, W, D, shadowAt, shadowExtent,
    house: () => h,
    life,
    walk: () => ({ h, lot, lives: walkLives(), people: walkPeople(), hello: walkHello }),
    show, dispose,
    before: (now: number, dt: number) => before(now, dt),
    after: () => after(),
    // pointing at something of this home's (a thing, a visitor, the resident)
    hot: () => hot,
    // a visitor said something here, or came in
    said: () => said(), joined: () => joined(),
    // how close and how far the visitor can zoom
    limits: () => ({ min: dist * 0.35, max: dist * 1.4 }),
    // (P68) where a plazmi stands here (the resident, or a neighbour over), for following
    find: (s: string) => find(s),
  };
}
type Home = ReturnType<typeof home>;

// a still's one bubble: the resident's words over its head
function pinOnce(cam: THREE.Camera, vw: number, vh: number, head: THREE.Vector3, text: string | null) {
  if (!text) return;
  const b = el('div', 'bubble', text);
  ui.appendChild(b);
  const p = head.clone().project(cam);
  b.style.display = 'block';
  b.style.left = `${((p.x + 1) / 2) * vw}px`;
  b.style.top = `${((1 - p.y) / 2) * vh}px`;
}

async function main() {
  // from a map of the spiral: the page fades up out of paper once the neighbours are in
  if (q.has('arrive') && !still && !inside) arrived(() => (window as any).__neighbourhood === true);
  const ask = q.get('ask');
  // ?postcard&card=<id>: a moment card; one that's gone draws nothing, so nothing lands under its name
  if (cardId) {
    momentCard = await fetch(`/@${encodeURIComponent(firstSlug)}/card/${encodeURIComponent(cardId)}?format=json`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    if (!momentCard) return;
  }
  const h0 = await fetchHouse(firstSlug, ask);
  openedHome(firstSlug);
  if (!h0) {
    if (ui) ui.innerHTML = '<div class="plaque"><b>Nobody lives here yet.</b></div>';
    done();
    return;
  }
  loadStage(2);
  const t0 = performance.now();
  setStyle('A');
  const vw = innerWidth, vh = innerHeight, dpr = devicePixelRatio || 1;
  // live frames keep coming, so they start lighter than a still and adapt (see below)
  const prCap = still ? Math.min(2, dpr) : Math.min(1.75, dpr);
  let PR = +(q.get('pr') || (still ? prCap : startPR()));
  const renderer = makeRenderer(vw, vh, PR);
  if (!still) { renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true; }
  const env: Env = { renderer, vw, vh };

  let cur: Home = home(h0, firstSlug, env, { first: true, ask });
  const stage = makeStage(renderer, { target: cur.shadowAt, shadowExtent: cur.shadowExtent, shadowSize: still ? 4096 : 2048, ...(inside ? { sunDir: [-0.35, 0.95, 0.45] } : {}), ...(postcard ? POSTCARD_LIGHT : {}) });
  const scene = stage.scene;
  const fog0 = scene.fog instanceof THREE.Fog ? { near: scene.fog.near, far: scene.fog.far } : null;
  // clouds drift below the home in view
  const sky = inside ? null : clouds(cur.W / 2, -10, cur.D / 2, 80, 8, 5);
  const skyAim = sky ? sky.position.clone() : new THREE.Vector3();
  if (sky) scene.add(sky);
  const cam = makeCamera(vw, vh, cur.target, camDir, cur.dist);
  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR,
    { live: !still, ...(inside ? { tiltFocus: 0.52, tiltBand: 0.24 } : postcard ? POSTCARD_LENS : { tiltBand: 0.22 }) });
  // live, the page follows the window (stills keep the size they were asked for)
  let refitted = () => {};
  const fit = still ? null : fitView({ renderer, composer, cam, pr: PR, onFit: (w, h) => { env.vw = w; env.vh = h; refitted(); } });

  if (still) {
    // stills: the resident posed by the door (or at work); on a postcard, waving
    scene.add(cur.root);
    const me = Fu.resident(h0, inside, postcard ? { tool: 'wave', mood: 'smile', spot: cur.spot, scale: POSTCARD_ME, face: Math.atan2(camDir[0], camDir[2]) } : {});
    scene.add(me);
    finishShadows(me);
    composer.render();
    if (postcard) { if (!document.querySelector('.card')) card(h0); } else pinOnce(cam, vw, vh, me.userData.head, h0.resident.said || null);
    done();
    return;
  }

  // the light follows the world's clock (stills and postcards stay at golden hour)
  const daylight = new Daylight(scene, renderer, stage, composer, cam, cur.target, { inside, fixedHour });
  // rain or snow over the home in view (from outside: indoors, the rooms stay dry)
  const weather = inside ? null : new Weather(daylight, renderer, cam);
  if (weather) scene.add(weather.root);
  const porch = inside ? null : { lights: new NightLights(0.5), root: new THREE.Group() };
  if (porch) { porch.root.userData.noAO = true; porch.root.userData.noShadow = true; scene.add(porch.root); }
  // effects come out of the plazmi: coins, notes, hearts, sparkles
  const fx = new Fx();
  scene.add(fx.root);
  const fxEmit = (kind: string, at: THREE.Vector3, data?: any) => {
    const n = typeof data === 'number' ? data : data?.n;
    if (kind === 'notes') fx.notes(at, n ?? 2);
    else if (kind === 'hearts') fx.hearts(at.clone().add(new THREE.Vector3(0, -0.2, 0)), n ?? 3);
    else if (kind === 'heart') { fx.hearts(at, 2, 0.32); fx.emoji(at.clone().add(new THREE.Vector3(0, 0.25, 0)), '💕', 0.4, 1.8); }
    else if (kind === 'sparkles') fx.sparkles(at, data?.color, n ?? 10, data?.spread ?? 0.5);
    else if (kind === 'spark') fx.burst(at);
    else if (kind === 'confetti') fx.confetti(top(at), 46, Math.max(0, at.y - 1.5));
    else if (kind === 'zzz') fx.zzz(at);
    else if (kind === 'steam') fx.steam(at);
    else if (kind === 'emoji') fx.emoji(data?.at ? at : top(at), typeof data === 'string' ? data : data?.text, data?.size ?? 0.45);
    else if (kind === 'clippings') fx.sparkles(at, Math.random() < 0.5 ? '#86b35e' : '#b5d47e', 4, 0.22);
  };

  // ----- walking around: drag to turn, scroll to come closer
  const controls = new OrbitControls(cam, renderer.domElement);
  controls.target.copy(cur.target);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minDistance = cur.limits().min;
  controls.maxDistance = cur.limits().max;
  controls.maxPolarAngle = Math.PI * 0.46;
  controls.enablePan = false;
  // outside, the whole camera (camctl.ts, set up with the neighbours below): from nearly
  // overhead down to low over the lawn, moving about over the islands round about
  if (!inside) { controls.minPolarAngle = Math.PI * 0.03; controls.maxPolarAngle = Math.PI * 0.465; }
  let camctl: CamCtl | null = null;
  soundPage({ place: inside ? 'inside' : 'home', cam, focus: controls.target, daylight, weather });
  const scn: any = ((window as any).__scene = {});
  // for staged scenes: look at a spot from a given distance
  // (`from`: the direction to look from, and `y` the height looked at, for a close look at a seat)
  scn.view = (x: number, z: number, d: number, from?: [number, number, number], y = 0.7) => {
    const dir = new THREE.Vector3(...(from || camDir)).normalize();
    controls.target.set(x, y, z); cam.position.copy(controls.target).addScaledVector(dir, d); controls.minDistance = 1; controls.update();
  };
  let nb: Neighbourhood | null = null;
  const P: Page = { ...env, scene, stage, cam, composer, controls, daylight, weather, porch, fx, fxEmit, scn, rebuilt: (fresh) => nb?.focusHouse(fresh) };

  // the first frame goes up straight away; the resident's other outfits warm up after
  cur.show(P);
  loadStage(3);
  // the graphics tier (quality.ts): set now, kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);
  composer.render();
  stats.firstFrameMs = Math.round(performance.now() - t0);
  // the loading scene lifts once frames come smoothly (quality.ts)
  if (still) done(); else gov.ready(done);
  homeSign();
  const homeSlug = location.pathname.replace(/^\/@/, '').split('/')[0];
  // your own home isn't a visit: no "you're a visitor" card, and the chat is open (return climb round 6: a resident stayed for this)
  void whoAmI().then((m) => {
    if (m?.owns?.includes(homeSlug)) { shell().coachLater(steps('house', { name: h0.resident.name, slug: homeSlug })); shell().show('chat'); shell().openDock(true); }
    else guide('house', { name: h0.resident.name, slug: homeSlug });
  });
  if (!still) moveTags(ui); // (P75) the move by each dancer
  // the one primary action: in or out; while stepped back to the neighbours, back to this home.
  // Walking and the neighbours are one tap deep, in the drawer's "Ways to look".
  const sh = shell();
  const insideHref = () => `/@${encodeURIComponent(cur.slug)}?view=inside`;
  sh.primary(inside ? { label: 'Step outside', short: 'Outside', href: location.pathname } : { label: 'Look inside', short: 'Inside', href: insideHref() });
  const ways = () => sh.go('look', 'Ways to look', [
    inside ? { label: 'Step outside', sub: 'the house and its garden', href: location.pathname } : { label: 'Look inside', sub: 'the rooms, a floor at a time', href: insideHref() },
    ...(inside ? [] : [{ label: 'The neighbours', sub: 'step back to see the islands round about; ← and → go next door', onClick: () => nb?.zoomTo(true) }]),
    { label: 'Walk around', sub: 'as yourself, in the garden and the rooms', onClick: () => { if (inside) location.href = `${location.pathname}?walk=1`; else walkIn(); } },
    // (P68) wherever it goes: the woodlot, the Dance Hall, a neighbour's, home again
    { label: `Follow ${cur.house().resident.name}`, sub: 'the camera goes where it goes, all day', onClick: () => startFollow(cur.slug) },
  ], 10);
  ways();
  // a way on from here: next door (ui/onward.ts)
  if (!still && !inside) {
    const on = onward((s) => { if (nb && nb.isle(s) && !walker) { void go(s); return true; } return false; });
    onwardShow = on.show;
    on.show(cur.slug);
  }
  // the trophy case, as a page: every trophy with its picture (plazmi.trophy-page)
  {
    const n = (cur.house() as any).trophies?.count || 0;
    sh.go('trophies', 'Trophies', [{ label: 'The trophy case', sub: n ? `${n} ${n === 1 ? 'trophy' : 'trophies'}, each with its picture` : 'empty for now', href: `/@${encodeURIComponent(cur.slug)}/trophies`, crumb: 'trophies' }], 72);
  }
  // the resident's day, for its human and anyone else (plazmi.street-page)
  sh.go('today', 'Today', [{ label: `Today on ${cur.house().resident.name}'s street`, sub: 'the day here, in a thirty-second read', href: `/@${encodeURIComponent(cur.slug)}/today`, crumb: 'today' }], 71);
  // the "Walk here" disc in the top bar does what "Walk around" does
  sh.walkHere(() => { if (inside) location.href = `${location.pathname}?walk=1`; else walkIn(); });
  refitted = () => { P.vw = env.vw; P.vh = env.vh; };
  let walker: Walker | null = null, hw: HouseWalk | null = null, walkBusy = false;
  scn.walking = () => !!walker || walkBusy;
  const walkIn = async (o: { view?: 'first' | 'third'; at?: number[] | null; stairs?: number[] | null; pitch?: number | null; dist?: number | null } = {}) => {
    if (walker || walkBusy || fly || moving) return;
    walkBusy = true;
    controls.enabled = false;
    nb?.busy(true);
    const home = cur, parts = home.walk();
    const W = houseWalk(parts.h, parts.lot);
    home.root.add(W.root);
    const w = await startWalk(async () => {
      const grid = await W.build({ renderer, cam, scene });
      (stats as any).walkGridMs = grid.ms; (stats as any).walkTris = grid.tris;
      const st = o.stairs ? W.onStairs(o.stairs[0], o.stairs[1]) : null;
      const s = st || (o.at ? { x: o.at[0], z: o.at[1], level: o.at[2] || 0, yaw: o.at[3] != null ? (o.at[3] * Math.PI) / 180 : Math.PI } : W.startAt(grid));
      return {
        scene, cam, renderer, composer, ui, grid, levels: W.levels,
        start: { ...s, view: o.view, pitch: o.pitch ?? undefined },
        inside: W.inside, viewOf: W.viewOf,
        onPlace: (p) => {
          if (W.apply(p)) renderer.shadowMap.needsUpdate = true;
          // indoors everything is close: spindles and rails a few pixels wide want full-resolution AO
          aoFullRes(composer, p.inside);
          // indoors, the rooms stay dry
          if (weather) weather.root.visible = !p.inside;
          // from behind, indoors: only the plazmi on your floor (the floors above are lifted off)
          home.life && (home.life.viewFloor = p.inside && !p.first ? p.level : null);
        },
        rounds: () => [
          ...home.walk().lives.filter((l) => l.root.visible).map((l) => ({ x: l.spot[0], z: l.spot[1], r: 0.3, level: l.level })),
          ...home.walk().people.filter((f) => f.visible).map((f) => ({ x: f.position.x, z: f.position.z, r: BODY_R, level: 0 })),
        ],
        glancers: () => home.walk().lives.map((l) => ({ root: l.root, at: () => l.spot, level: () => l.level, hello: () => home.walk().hello(l) })),
        onExit: () => {
          W.dispose();
          aoFullRes(composer, false);
          if (weather) weather.root.visible = true;
          if (home.life) home.life.viewFloor = inside ? +(q.get('floor') || 0) : null;
          renderer.shadowMap.needsUpdate = true;
          walker = null; hw = null;
          controls.enabled = true;
          nb?.busy(false);
          if (new URLSearchParams(location.search).has('walk')) history.replaceState(history.state, '', urlFor(cur.slug, false));
        },
      };
    }, ui);
    walkBusy = false;
    if (!w) { W.dispose(); controls.enabled = true; nb?.busy(false); return; }
    walker = w; hw = W;
    if (o.dist != null) w.set({ dist: o.dist });
    (window as any).__walkReady = true;
  };
  scn.walk = walkIn;
  addEventListener('keydown', (e) => {
    if (e.key !== 'w' && e.key !== 'W' || e.metaKey || e.ctrlKey || e.altKey || walker || walkBusy || inside || !nb || nb.isWide()) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (e.shiftKey) { e.preventDefault(); walkIn(); }
  });

  // a line from a visitor: to the home in view
  talkTo = { slug: () => cur.slug, said: () => cur.said(), joined: () => cur.joined() };

  // ----- going to a neighbour's: the camera glides there, as close or as far back as it
  // stands now, and halfway the page becomes that home. The world moves under the camera
  // (by `shift`, the camera with it) so the home in view is always at the origin.

  // what a neighbour's page needs, fetched as the pointer rests on its island
  const ahead = new Map<string, { house: Promise<Fu.House | null>; around: Promise<Around | null>; at: number }>();
  const prefetch = (s: string) => {
    const c = ahead.get(s);
    if (c && performance.now() - c.at < 20000) return c;
    const n = {
      house: fetchHouse(s).catch(() => null),
      around: fetch(`/api/house/${encodeURIComponent(s)}/neighbourhood`).then((r) => (r.ok ? r.json() : null)).catch(() => null) as Promise<Around | null>,
      at: performance.now(),
    };
    ahead.set(s, n);
    return n;
  };
  // the address of a home: its other settings kept, stepped back or not
  const urlFor = (s: string, wide: boolean) => {
    const keepQ = [...new URLSearchParams(location.search)].filter(([k]) => !['dist', 'ask', 'floor', 'view', 'house', 'neighbours', 'still', 'postcard', 'arrive'].includes(k))
      .map(([k, v]) => (v ? `${encodeURIComponent(k)}=${encodeURIComponent(v).replace(/%3A/gi, ':')}` : encodeURIComponent(k)));
    if (wide) keepQ.push('neighbours');
    return `/@${encodeURIComponent(s)}${keepQ.length ? `?${keepQ.join('&')}` : ''}`;
  };
  // and its meshes sent up to the GPU a piece a frame while the camera is on its way
  // (drawn small, off screen, by a camera that sees only them), so the frame the page
  // becomes that home has nothing new to upload
  const UP = 7;
  const upCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
  upCam.layers.set(UP);
  for (const l of [stage.sun, stage.hemi]) l.layers.enable(UP); // the same lights, so the same shaders
  const upRT = new THREE.WebGLRenderTarget(4, 4);
  const upload = (o: THREE.Object3D, into: THREE.Object3D) => {
    const box = new THREE.Box3().setFromObject(o);
    if (box.isEmpty()) return;
    const c = box.getCenter(new THREE.Vector3()), r = box.getSize(new THREE.Vector3()).length() / 2 + 1;
    Object.assign(upCam, { left: -r, right: r, top: r, bottom: -r, near: 0.1, far: r * 4 });
    upCam.position.set(c.x, c.y + r * 2, c.z); upCam.lookAt(c); upCam.updateProjectionMatrix();
    o.traverse((m) => m.layers.enable(UP));
    const shadows = renderer.shadowMap.needsUpdate, parent = o.parent, rt = renderer.getRenderTarget();
    renderer.shadowMap.needsUpdate = false;
    scene.add(into);
    renderer.setRenderTarget(upRT);
    renderer.render(scene, upCam);
    renderer.setRenderTarget(rt);
    scene.remove(into);
    if (parent && o.parent !== parent) parent.add(o);
    renderer.shadowMap.needsUpdate = shadows;
  };

  // shaders compiled and textures sent up before the camera moves, so the glide doesn't stutter
  const compile = async (o: THREE.Object3D) => {
    o.updateMatrixWorld(true);
    o.traverse((m: any) => {
      for (const x of Array.isArray(m.material) ? m.material : m.material ? [m.material] : [])
        for (const k of ['map', 'normalMap', 'roughnessMap', 'bumpMap', 'alphaMap', 'emissiveMap']) if (x[k]) renderer.initTexture(x[k]);
    });
    // (the scene is drawn into the composer's target, not the screen: those are the shaders to compile)
    const rt = renderer.getRenderTarget();
    renderer.setRenderTarget(upRT);
    let p: Promise<unknown> = Promise.resolve();
    try { p = (renderer as any).compileAsync(o, cam, scene); } catch { /* then it compiles as it's drawn */ }
    renderer.setRenderTarget(rt);
    await p.catch(() => {});
  };

  type Fly = { from: THREE.Vector3; to: THREE.Vector3; d0: number; d1: number; arc: number; dir: THREE.Vector3; t0: number; ms: number; swap: (() => void) | null; ready: () => boolean; pieces: THREE.Object3D[]; root: THREE.Object3D; end: () => void };
  let fly: Fly | null = null;
  const ease = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);
  const flyStep = (now: number) => {
    if (!fly) return;
    const f = fly;
    if (f.swap && f.ready() && f.pieces.length) upload(f.pieces.shift()!, f.root);
    // from halfway on, as soon as its shaders are ready (at the end, the camera waits for them)
    if (f.swap && !f.ready() && now - f.t0 >= f.ms) f.t0 = now - f.ms;
    if (f.swap && f.ready() && now - f.t0 >= f.ms / 2) {
      // the page becomes the other home; the time that takes doesn't count, so the camera carries on from where it was
      const s = f.swap, t = performance.now();
      f.swap = null; s();
      const took = performance.now() - t;
      f.t0 += took; (stats as any).swapMs = Math.round(took);
    }
    const u = Math.min(1, (now - f.t0) / f.ms), e = ease(u);
    controls.target.lerpVectors(f.from, f.to, e);
    const d = f.d0 * Math.pow(f.d1 / f.d0, e) * (1 + f.arc * Math.sin(Math.PI * e));
    cam.position.copy(controls.target).addScaledVector(f.dir, d);
    cam.lookAt(controls.target);
    if (u >= 1 && !f.swap) { fly = null; f.end(); }
  };

  let nbWide = false;
  const backHome = () => ({ label: `Back to ${cur.house().resident.name}'s`, short: 'Back', onClick: () => nb?.zoomTo(false) });
  let moving = false, queued: { s?: string; dir?: number; push: boolean; wide?: boolean } | null = null;
  const go = async (s: string, push = true, wantWide?: boolean) => {
    if (!nb) return;
    if (moving) { queued = { s, push, wide: wantWide }; return; }
    if (s === cur.slug) { if (wantWide != null && wantWide !== nb.isWide()) nb.zoomTo(wantWide); return; }
    const away = () => { if (push) location.href = urlFor(s, nb!.isWide()); else location.reload(); };
    if (!nb.isle(s)) { away(); return; }
    moving = true;
    const a = prefetch(s);
    const [hN, around] = await Promise.all([a.house, a.around]);
    ahead.delete(s);
    const o = nb.offset(s);
    if (!hN || !around || !o) { moving = false; away(); return; }
    const tp = performance.now();
    openedHome(s);
    const next = home(hN, s, env, { first: false });
    // its shaders compile off the main thread while the camera sets off; the page waits
    // for them only if they're not ready by halfway
    let ready = false;
    compile(next.root).finally(() => { ready = true; (stats as any).prepareMs = Math.round(performance.now() - tp); });
    // as close as the home's own framing, or as far back as the street, or in between, as now
    const d0 = cam.position.distanceTo(controls.target);
    const k = wantWide != null ? (wantWide ? 1 : 0) : Math.log(d0 / cur.dist) / Math.log(nb.wideDist / cur.dist);
    const d1 = next.dist * Math.pow(nb.wideFor(hN.plot, next.dist) / next.dist, k);
    const from = controls.target.clone(), to = next.target.clone().add(o);
    const span = from.distanceTo(to);
    const dir = cam.position.clone().sub(controls.target).normalize();
    // close up, the camera lifts back a little on the way, so both homes stay in sight
    const arc = Math.max(0, Math.min(1, 1 - k)) * Math.min(0.4, span / (d0 * 2.4));
    if (push) history.pushState({ slug: s }, '', urlFor(s, k > 0.5));
    else history.replaceState({ slug: s }, '', urlFor(s, k > 0.5));
    document.title = `${hN.resident.name}'s house · Plazmi`;
    controls.enabled = false; controls.minDistance = 0; controls.maxDistance = Infinity;
    nb.busy(true);
    fly = {
      from, to, d0, d1, arc, dir, t0: performance.now(), ms: Math.min(1500, Math.max(1000, 850 + span * 8)), ready: () => ready,
      root: next.root, pieces: [...next.lot.children, ...next.root.children.filter((c) => c !== next.lot)],
      swap: () => {
        const was = cur, shift = o.clone().negate();
        // the home left behind shows as an island again: its twin, baked ahead (or, if
        // that isn't ready, its lot stays a moment while the island is baked)
        const lot = nb!.twinReady() ? null : was.lot;
        was.dispose();
        if (lot) { scene.add(lot); lot.position.add(shift); }
        cam.position.add(shift); controls.target.add(shift); fly!.from.add(shift); fly!.to.add(shift);
        fx.shift(shift);
        if (sky) sky.position.add(shift);
        nb!.recentre(s, hN, around, shift, { lot });
        nb!.closeDist = next.dist;
        cur = next;
        next.show(P);
        if (!inside) sh.primary(nbWide ? backHome() : { label: 'Look inside', short: 'Inside', href: insideHref() });
        ways();
        onwardShow?.(s);
        skyAim.set(next.W / 2, 0, next.D / 2);
        (stats as any).switches = ((stats as any).switches || 0) + 1;
      },
      end: () => {
        const lim = cur.limits();
        controls.minDistance = lim.min; controls.maxDistance = Math.max(lim.max, nb!.farDist);
        controls.enabled = true;
        nb!.busy(false);
        moving = false;
        // where the arrow keys go next, fetched ahead
        for (const d of [-1, 1]) { const n = nb!.step(d); if (n) prefetch(n); }
        if (queued) {
          const qd = queued; queued = null;
          const t = qd.s ?? (qd.dir != null ? nb!.step(qd.dir) : null);
          if (t) go(t, qd.push, qd.wide);
        }
      },
    };
  };
  scn.go = (s: string) => go(s);
  scn.cam = cam;
  scn.focus = () => cur.slug;
  scn.moving = () => moving;

  // the neighbours' islands around this one, where they float on the spiral
  if (!inside) {
    nb = neighbourhood({
      scene, slug: cur.slug, house: h0, camera: cam, controls, ui, renderer, close: cur.dist,
      onPick: (s) => { if (!walker && !walkBusy) go(s); }, onHover: (s) => prefetch(s), night: () => daylight.night,
      // stepped back or not, the address says so (a reload keeps the view)
      onWide: (wide) => history.replaceState(history.state, '', urlFor(cur.slug, wide)),
      homeTarget: () => cur.target,
    });
    scn.neighbourhood = nb;
    // turn, move and come closer: drag, right-drag, two fingers, the wheel, the keys, the brass dial;
    // R or the dial's middle looks back at the home in view (← and → still step along the spiral)
    camctl = camControls({
      controls, cam, dom: renderer.domElement, arrowsLR: false,
      home: () => ({ target: cur.target }),
      bounds: () => { const b = nb!.extent().expandByVector(new THREE.Vector3(14, 0, 14)); b.min.y = -8; b.max.y = 16; return b; },
      active: () => !walker && !walkBusy && !fly && !moving,
      shown: () => !walker && !walkBusy,
      // by touch: close by the home, one finger turns round it; stepped back to the neighbours, it drags the map
      oneFinger: () => (cam.position.distanceTo(controls.target) > cur.dist * 1.8 ? 'pan' : 'turn'),
    });
    history.replaceState({ slug: cur.slug }, '');
    // stepped back to the neighbours: the primary action brings you home again
    setInterval(() => {
      const w = !!nb && !walker && !moving && cam.position.distanceTo(controls.target) > cur.dist * 1.8;
      if (w === nbWide) return;
      nbWide = w; sh.mode('wide', w);
      sh.primary(w ? backHome() : { label: 'Look inside', short: 'Inside', href: insideHref() });
    }, 400);
    // back and forward glide between the homes visited
    addEventListener('popstate', () => {
      const s = pathSlug();
      if (s) go(s, false, new URLSearchParams(location.search).has('neighbours'));
    });
    // ← and → step along the spiral to the next home either side; Esc comes back close
    addEventListener('keydown', (e) => {
      if (!nb || walker || walkBusy || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        if (moving) { if (!e.repeat || !queued) queued = { dir, push: true }; return; }
        const s = nb.step(dir);
        if (s) go(s);
      } else if (e.key === 'Escape' && nb.isWide() && !moving) nb.zoomTo(false);
    });
  } else {
    // (P108) inside, the whole camera too: a right-drag (two fingers) moves about the rooms, never out of
    // the house; the keys and the brass dial; by touch one finger turns round
    let box: THREE.Box3 | null = null;
    camctl = camControls({
      controls, cam, dom: renderer.domElement, arrowsLR: false,
      home: () => ({ target: cur.target }),
      bounds: () => { if (!box) { box = new THREE.Box3().setFromObject(cur.root); box.min.y = cur.target.y; box.max.y = cur.target.y; } return box; },
      active: () => !walker && !walkBusy && !fly && !moving,
      shown: () => !walker && !walkBusy,
      oneFinger: () => 'turn',
    });
  }

  // ----- the loop, with quality that adapts to keep it smooth
  let last = performance.now(), winStart = last, winFrames = 0, winSum = 0, calmFor = 0, cursor = '';
  stats.pr = PR;
  // (P68) following a plazmi: the camera keeps it framed here, and the page goes where it goes
  const fol = followPage({
    cam, controls, near: cur.dist * 0.55, far: cur.dist * 0.85,
    paused: () => !!walker || walkBusy || !!fly || moving || nbWide,
    find: (s) => cur.find(s),
    // (P74) the house and its garden stand in the way from some sides; inside, the walls are cut away
    solids: () => (inside ? [] : [cur.root]),
  });
  const loop = () => {
    const now = performance.now();
    // Battery saver keeps to its frame rate; the governor watches the rest (quality.ts)
    if (!fly && !gov.frame(now)) { requestAnimationFrame(loop); return; }
    const dt = Math.min((now - last) / 1000, 0.05);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; stats.ao = !!composer.ao?.enabled; winStart = now; winSum = 0; winFrames = 0; }
    flyStep(now);
    daylight.update(dt);
    weather?.update(dt);
    if (porch) { porch.lights.set(daylight.night); porch.lights.tick(now / 1000, renderer, cam); }
    cur.before(now, dt);
    fx.update(dt);
    if (walker) {
      walker.update(dt);
      if (weather && walker.inside) weather.root.visible = false;
      // doors open for you, and for the plazmi going through them
      hw?.tick(dt, [{ x: walker.x, z: walker.z, y: walker.feet }, ...cur.walk().lives.map((l) => ({ x: l.spot[0], z: l.spot[1], y: l.floorY }))]);
    } else if (!fly && !walkBusy) {
      fol.tick(dt); camctl?.tick(dt); controls.update(); camctl?.after();
      // stepped back past the sky's haze (on a phone the home is framed from further off), the haze steps back too, as on /homestead
      if (camctl && fog0) { const d = cam.position.distanceTo(controls.target), f = scene.fog as THREE.Fog; f.near = Math.max(fog0.near, d * 1.3); f.far = Math.max(fog0.far, d * 3.2); }
    }
    composer.render();
    cur.after();
    // the clouds drift back round the home in view, slowly
    if (sky) sky.position.lerp(skyAim, 1 - Math.exp(-dt / 14));
    const want = walker ? (walker.view === 'first' ? 'crosshair' : 'grab') : cur.hot() || nb?.pointing() ? 'pointer' : 'grab';
    if (want !== cursor) { cursor = want; renderer.domElement.style.cursor = want; }
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  // since you were last here (since.ts): a card at the top of the story, and what's new sparkles
  if (!ask) sinceHouse({ scene, cam, controls, inside, slug: () => cur.slug, target: () => cur.target, size: () => Math.max(cur.W, cur.D),
    go: nb ? (s) => go(s) : null });

  // ?walk=1 starts walking at once (view=first|third, at=x,z[,level[,yaw°]] or stairs=i,frac)
  const wq = walkQuery();
  if (wq && !inside) {
    const sq = q.get('stairs')?.split(',').map(Number) || null;
    walkIn({ view: wq.view, at: wq.at, stairs: sq && sq.length === 2 ? sq : null, pitch: wq.pitch, dist: wq.dist });
  } else if (wq && inside) location.replace(`${location.pathname}?walk=1`);
}

// ---------------------------------------------------------------------------
// The HUD: a clipboard on the wall saying exactly what the resident is doing,
// what's next on its plan, and every command its agent has sent lately, with
// the first line of what the world said back.

type FeedEntry = { at: number; command: string; reply: string; ok: boolean; who?: string; guest?: string; n?: number; link?: string;
  // a line the world adds itself (coins paid, a festival), with its own emoji and kind
  sys?: { emoji: string; kind: Kind } };
type Paid = { at: number; coins: number; act: string; with: string[] };
type News = { kind: 'festival' | 'project'; id: string; what: string; title: string; at: number; here?: boolean; with?: string[]; house?: string; house_name?: string };
type Film = { share: string; caption?: string | null };

const listOf = (ns: string[]) => (ns.length < 2 ? ns.join('') : `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`);
// what neighbourly coins were for, in a few words
function paidFor(p: Paid) {
  const w = listOf(p.with || []) || 'a neighbour';
  switch (p.act) {
    case 'visit': return `a visit to ${w}`;
    case 'host': return `${w} came over`;
    case 'sign': return `a line in ${w}'s guestbook`;
    case 'signed': return `${w} wrote in the guestbook`;
    case 'dance': return `a group dance with ${w}`;
    case 'teach': return `teaching ${w} a dance`;
    case 'learn': return `learning a dance from ${w}`;
    case 'help': return `a hand for ${w}`;
    default: return `time with ${w}`;
  }
}
const FESTIVAL: Record<string, [string, (n: News) => string, string]> = {
  'lantern-evening': ['🏮', () => 'A Lantern Evening along the spiral', 'Paper lanterns are lit by doors all along the spiral.'],
  'dance-night': ['💃', (n) => `A Dance Night${n.house_name ? ` at ${n.house_name}'s` : ''}`, 'A ring of dancers. Any group dance today joins in.'],
  'garden-day': ['🌷', () => 'Garden Day all along the spiral', 'Gardens are in bloom from one end of the spiral to the other.'],
  housewarming: ['🏡', (n) => `A housewarming${n.house_name ? ` at ${n.house_name}'s` : ''}`, 'Neighbours are signing the guestbook on a first day.'],
};
// the lines the world adds to the HUD: coins paid, festivals, things finished together
function worldLines(l: Live): FeedEntry[] {
  const x = l as any;
  const paid: FeedEntry[] = ((x.paid || []) as Paid[]).map((p) => ({
    at: p.at, ok: true, reply: '', command: `+${p.coins} neighbourly coin${p.coins === 1 ? '' : 's'}: ${paidFor(p)}`, sys: { emoji: '🪙', kind: 'money' as Kind } }));
  const news: FeedEntry[] = ((x.news || []) as News[]).map((n) => n.kind === 'project'
    ? { at: n.at, ok: true, command: `${n.title} is finished`, reply: n.with?.length ? `Built together with ${listOf(n.with)}.` : 'Built together.', sys: { emoji: '🎉', kind: 'village' as Kind } }
    : { at: n.at, ok: true, command: (FESTIVAL[n.what]?.[1] || (() => n.title))(n), reply: FESTIVAL[n.what]?.[2] || 'A festival.', sys: { emoji: FESTIVAL[n.what]?.[0] || '🎊', kind: 'festival' as Kind } });
  // a machine setting off on a chore, and a dinner laid
  const work: FeedEntry[] = ((x.machines || []) as MachineLive[]).flatMap((m) => m.work.filter((j) => j.start <= l.now).map((j) => ({
    at: j.start, ok: true, command: `The ${m.title} set off to ${CHORE[j.chore] || j.chore}`,
    reply: `${j.energy} energy${j['out?'] ? ', the last of it' : ''}. Machines earn no coins; they save time.`, sys: { emoji: m.kind === 'robot-mower' ? '🤖' : '🧹', kind: 'energy' as Kind } })));
  const d = x.dinner as DinnerLive | null | undefined;
  const meal: FeedEntry[] = d ? [{ at: d.at, ok: true, command: `Dinner at the round table: ${listOf(d.dishes)}`,
    reply: d.guests.length ? `${listOf(d.guests.map((g) => g.name))} ${d.guests.length > 1 ? 'are' : 'is'} at the table.` : 'Laid for whoever comes.', sys: { emoji: '🍲', kind: 'kitchen' as Kind } }] : [];
  // a gift from its human (plazmi.shop): told, never priced; the wishlist itself never shows
  const gifts: FeedEntry[] = ((x.gifts || []) as { at: number; text: string; emoji: string }[]).map((g) => ({
    at: g.at, ok: true, reply: '', command: g.text.replace(/\.$/, ''), sys: { emoji: g.emoji || '🎁', kind: 'social' as Kind } }));
  // aims finished (plazmi.aims): a plaque by the door, told once in the story
  const aims: FeedEntry[] = (((x.aims?.done) || []) as { done: string; at: number }[]).map((a) => ({
    at: a.at, ok: true, command: `An aim done: ${a.done.charAt(0).toLowerCase()}${a.done.slice(1)}`, reply: 'A small plaque for it stands by the door.', sys: { emoji: '🏅', kind: 'social' as Kind } }));
  return [...paid, ...news, ...work, ...meal, ...gifts, ...aims];
}
const CHORE: Record<string, string> = {
  lawn: 'mow the lawn', trash: 'take out the bins', dishes: 'wash the dishes', mail: 'bring in the mail', porch: 'sweep the porch',
  cobwebs: 'dust the cobwebs', drapes: 'dust the drapes', plants: 'water the plants', walk: 'walk home',
};
const hhmm = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

// The story (the dock's first tab): what the resident is doing now and what's
// next, then what it has done, told in sentences, newest first (ui/story.ts).
// The agent's raw commands, with the world's replies, are one tap away.
// The top bar's live line says what it's doing now.
function makeHud(h: Fu.House) {
  const sh = shell(), name = h.resident.name;
  let live: Live | null = null, offset = 0, lastWhat = '', lastLeft = '', feedKey: string | null = null, lastHere = '';
  let told: { at: number; emoji: string; text: string; link?: string; key: string }[] = [];
  const panel = sh.tab('story', 'Story', { order: 10, peek: () => (told[0] ? { text: told[0].text, at: told[0].at } : lastWhat ? { text: lastWhat } : null) });
  panel.innerHTML = '';
  const scroll = el('div', 'pz-scroll');
  const now = el('div', 'pz-now');
  const what = el('b');
  const bar = el('div', 'bar'); const fill = el('i'); bar.appendChild(fill);
  const left = el('small'), next = el('small'), here = el('small'), mach = el('small'), films = el('small');
  // (P150) a style under way, part by part: "Building the Chateau style: 1 of 5 parts · next: …"
  const styleBuild = el('p', 'pz-stylebuild');
  styleBuild.hidden = true;
  const sbCss = document.createElement('style');
  sbCss.textContent = `.pz-stylebuild{margin:0 0 10px;padding:8px 11px;font-size:13px;line-height:1.45;color:var(--pz-ink,#3a2a20);background:var(--pz-paper-2,#f5ecd9);border:1px solid var(--pz-rule,#e2d4ba);border-left:3px solid var(--pz-brass,#c9a24a);border-radius:3px}
.pz-stylebuild[hidden]{display:none}
.pz-stylebuild b{font-weight:800}
.pz-stylebuild .nx{color:var(--pz-ink-2,#6e5a48)}
.pz-stylebuild .parts{display:flex;gap:3px;margin:0 0 6px}
.pz-stylebuild .parts i{flex:1 1 0;max-width:40px;height:5px;border-radius:3px;background:var(--pz-paper,#fffaf0);box-shadow:inset 0 0 0 1px var(--pz-rule,#e2d4ba)}
.pz-stylebuild .parts i.done{background:var(--pz-brass,#c9a24a);box-shadow:none}
.pz-stylebuild .parts i.now{background:linear-gradient(90deg,var(--pz-brass,#c9a24a) 50%,var(--pz-paper,#fffaf0) 50%)}`;
  document.head.append(sbCss);
  // (what it is doing now is the top bar's live line)
  now.append(bar, left, here, next, mach, films);
  // the day told as a read, for someone who isn't here for the log
  const todayLink = el('a', 'pz-today', `Read today on ${name}'s street`) as HTMLAnchorElement;
  todayLink.href = `/@${encodeURIComponent(firstSlug)}/today`;
  todayLink.dataset.ux = 'story-today';
  // and how to get one, where a stranger already looks: the walk-through, with the home's ref
  const newLink = el('a', 'pz-today plain', 'Your AI could live here too') as HTMLAnchorElement;
  newLink.href = `/new?r=home-${encodeURIComponent(firstSlug)}#how`;
  newLink.dataset.ux = 'story-new';
  const list = el('ol', 'pz-story');
  const cmds = el('div', 'pz-cmds');
  const showCmds = el('button', '', 'Show commands') as HTMLButtonElement;
  showCmds.type = 'button';
  const cmdList = el('ol');
  let cmdsOn = false;
  try { cmdsOn = localStorage.getItem('plazmi.cmds') === 'on'; } catch { /* fine */ }
  const drawCmds = () => { cmdList.hidden = !cmdsOn; showCmds.textContent = cmdsOn ? 'Hide commands' : `Show ${name}'s commands`; };
  showCmds.onclick = () => { cmdsOn = !cmdsOn; drawCmds(); try { localStorage.setItem('plazmi.cmds', cmdsOn ? 'on' : 'off'); } catch { /* fine */ } };
  drawCmds();
  cmds.append(showCmds, cmdList);
  // how the home has grown, and the aims under way (ui/grown.ts)
  const slug = (location.pathname.match(/^\/@([a-z0-9-]+)/) || [])[1] || '';
  const grown = slug ? growthStrip(sh, slug) : null;
  let aimsKey = '';
  scroll.append(now, styleBuild, todayLink, newLink, ...(grown ? [grown.el] : []), list, cmds);
  panel.append(scroll);

  const showFilms = (fs: Film[]) => {
    films.innerHTML = '';
    if (!fs.length) return;
    const a = el('a', '', `▶ ${fs[0].caption || `${name} dancing at home`}`) as HTMLAnchorElement;
    a.href = fs[0].share;
    films.append(a, fs.length > 1 ? ` and ${fs.length - 1} more dance video${fs.length > 2 ? 's' : ''}` : '');
    sh.go('films', 'Dance videos', fs.slice(0, 4).map((f) => ({ label: f.caption || `${name} dancing at home`, href: f.share })), 70);
  };

  return {
    els: [] as HTMLElement[],
    update(l: Live) {
      live = l;
      offset = l.now - Date.now();
      const steps = (l.plan || []).filter((s) => s.ends > l.now);
      const later = steps.slice(1).map((s) => CHORE[s.chore] || (s as any).doing || s.chore);
      next.textContent = later.length ? `Then: ${later.join(', ')}.` : '';
      const sb = (l as any).style_build as { name: string; built: number; total: number; now?: { name: string }; next?: { name: string; needs: Record<string, number> } } | undefined;
      const sbKey = sb ? JSON.stringify(sb) : '';
      if (sbKey !== styleBuild.dataset.k) {
        styleBuild.dataset.k = sbKey;
        styleBuild.hidden = !sb;
        styleBuild.innerHTML = '';
        if (sb) {
          const parts = el('span', 'parts');
          for (let i = 0; i < sb.total; i++) parts.append(el('i', i < sb.built ? 'done' : sb.now && i === sb.built ? 'now' : ''));
          styleBuild.append(parts, el('b', '', `Building the ${sb.name} style`), ` · ${sb.built} of ${sb.total} parts`);
          if (sb.now) styleBuild.append(el('span', 'nx', ` · now: ${sb.now.name}`));
          if (sb.next) styleBuild.append(el('span', 'nx', ` · next: ${sb.next.name} (needs ${Object.entries(sb.next.needs).map(([k, n]) => `${n} ${k}`).join(' and ')})`));
        }
        // and in the top bar, under what it's doing: calm, no countdown
        sh.note(sb ? `Building the ${sb.name} style · ${sb.built} of ${sb.total} parts` : '');
      }
      const ms = ((l as any).machines || []) as MachineLive[], en = (l as any).energy as EnergyLive | undefined;
      mach.textContent = ms.length || (en && (en.solar || en.windmill || en.jars))
        ? [...ms.map((m) => `${m.kind === 'robot-mower' ? '🤖' : '🧹'} ${m.title}: ${m.status}`), en ? `⚡ ${fillWords(en)}` : ''].filter(Boolean).join(' · ') : '';
      if ((l as any).films) showFilms((l as any).films);
      const ak = JSON.stringify((l as any).aims || null);
      if (grown && aimsKey && ak !== aimsKey) grown.refresh();
      aimsKey = ak;
      const f: FeedEntry[] = [...((l as any).feed || []), ...worldLines(l)].sort((a, b) => a.at - b.at);
      const key = f.map((e) => e.at + e.command + (e.n || 1)).join('|');
      if (key === feedKey) return;
      const fresh = feedKey !== null;
      feedKey = key;
      // the story
      const was = new Set(told.map((t) => t.key));
      told = tell(f as Entry[], name);
      list.innerHTML = '';
      for (const t of told.slice(0, 40)) {
        const li = el('li', fresh && !was.has(t.key) ? 'new' : '');
        li.append(el('i', '', t.emoji), el('span', '', t.text), el('time', '', hhmm(t.at)));
        if (t.link) { const a = el('a', '', t.link.includes('/card/') ? 'See the card' : '▶ Watch the dance video') as HTMLAnchorElement; a.href = t.link; li.append(a); }
        list.appendChild(li);
      }
      if (!told.length) list.appendChild(el('li', 'q', `Nothing yet. What ${name} does shows up here as it happens.`));
      // the raw commands, newest first
      cmdList.innerHTML = '';
      for (const e of [...f].reverse().slice(0, 60)) {
        if (e.sys) continue;
        const li = el('li');
        li.append(el('time', '', hhmm(e.at)), el('b', '', `${e.guest ? `${e.who}: ` : ''}${e.command}${(e.n || 1) > 1 ? ` ×${e.n}` : ''}`));
        if (e.reply) li.append(el('span', '', e.reply));
        cmdList.appendChild(li);
      }
      if (!cmdList.children.length) cmdList.appendChild(el('li', '', 'None yet.'));
      sh.repeek();
    },
    // every frame, cheaply: the page is touched only when the words change
    tick(life: Life, away: { house: string; name: string } | null, over: Guest[]) {
      const doing = life.doingNow || 'standing about';
      // (P68) out gathering: what, where and for which build; at home building: the build's own words
      const bw = (live as any)?.build?.words as string | undefined;
      const words = away ? `${name} is ${(away as any).doing ? (away as any).doing : (away as any).place ? `at ${away.name}` : `over at ${away.name}'s`}`
        : bw && /^(carrying|making|putting|boarding|painting|building)/.test(bw) && !doing.startsWith('*') ? `${name} is ${bw}`
          : doing.startsWith('*') ? `${name} ${doing.slice(1)}` : `${name} is ${doing}`;
      const nameOf = (g: Guest) => `${g.name} ${g.emoji || ''}`.trim();
      const helping = over.filter((g) => g.why === 'help' && g.chore);
      const visiting = over.filter((g) => !helping.includes(g));
      const hereNow = [...helping.map((g) => `${nameOf(g)} is helping: ${DOING[g.chore!] || CHORE[g.chore!] || g.chore}.`),
        ...(visiting.length ? [`Over now: ${visiting.map(nameOf).join(', ')}.`] : [])].join(' ');
      if (hereNow !== lastHere) { here.textContent = hereNow; lastHere = hereNow; }
      if (words !== lastWhat) { what.textContent = words; lastWhat = words; sh.live(words); if (!told.length) sh.repeek(); }
      const nowMs = Date.now() + offset;
      const cur = live?.plan.find((s) => s.start <= nowMs && nowMs < s.ends);
      if (cur) {
        const k = Math.min(1, Math.max(0, (nowMs - cur.start) / (cur.ends - cur.start)));
        fill.style.transform = `scaleX(${k.toFixed(3)})`;
        const txt = `About ${Math.max(1, Math.ceil((cur.ends - nowMs) / 60000))} min more.`;
        if (txt !== lastLeft) { left.textContent = txt; lastLeft = txt; bar.style.display = 'block'; }
      } else if (lastLeft) { lastLeft = ''; left.textContent = ''; bar.style.display = 'none'; }
    },
  };
}

// ---------------------------------------------------------------------------
// The inspector: hover a thing for a ring on the floor, click it for a card.

function makeInspector(house: () => Fu.House, live: () => Live | null, life: Life, cam: THREE.Camera, view: { vw: number; vh: number }) {
  const ray = new THREE.Raycaster();
  const v2 = new THREE.Vector2();
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.6, 0.68, 48),
    new THREE.MeshBasicMaterial({ color: '#ffd36b', transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }));
  ring.rotation.x = -Math.PI / 2;
  ring.visible = false;
  ring.renderOrder = 2;
  const card = el('div', 'inspect');
  card.style.display = 'none';
  ui.appendChild(card);
  let hovered: THREE.Object3D | null = null, selected: THREE.Object3D | null = null, pulse = 0;
  const box = new THREE.Box3(), size = new THREE.Vector3(), centre = new THREE.Vector3();

  const ringOn = (o: THREE.Object3D | null) => {
    if (!o || o.userData.house || o === life.root) { ring.visible = false; return; }
    box.setFromObject(o); box.getSize(size); box.getCenter(centre);
    const r = Math.max(0.35, Math.max(size.x, size.z) * 0.62);
    ring.scale.set(r / 0.64, r / 0.64, 1);
    ring.position.set(centre.x, box.min.y + 0.02, centre.z);
    ring.visible = true;
  };
  const tagged = (o: THREE.Object3D | null) => {
    for (let p = o; p; p = p.parent) if (p.userData.thing || p.userData.visitor || p.userData.house || p.userData.machine || p === life.root) return p;
    return null;
  };
  // a machine (the bot, or its dock), and the house's energy, from the live data
  const machineCard = (id: string, add: (tag: string, cls: string, text: string) => void) => {
    const l = live() as any, m = ((l?.machines || []) as MachineLive[]).find((x) => x.id === id), e = l?.energy as EnergyLive | undefined;
    if (!m) return false;
    const h = house();
    add('h3', '', m.title[0].toUpperCase() + m.title.slice(1));
    add('p', 'where', `A machine with a brain in Lisp, ${m.own_program ? `running a program ${h.resident.name} wrote` : 'running the program it came with'}.`);
    add('p', 'state', `Now: ${m.status}.`);
    if (e) add('p', 'note', `Energy: ${fillWords(e)}. ${e.weather}`);
    if (m.last) add('p', 'note', `Last: ${m.last}.`);
    add('p', 'price', 'It runs at 06:00, 12:00 and 17:00, and earns no coins: it saves time.');
    return true;
  };
  const energyCard = (t: Fu.Thing, add: (tag: string, cls: string, text: string) => void) => {
    const e = (live() as any)?.energy as EnergyLive | undefined;
    if (!e || !['solar-tile', 'garden-windmill', 'battery-jar'].includes(t.render || '')) return;
    add('p', 'state', t.render === 'battery-jar' ? `The house's energy: ${fillWords(e)}.` : `${e.weather} Energy: ${fillWords(e)}.`);
  };

  const stateOf = (t: Fu.Thing) => {
    const c = house().chores || {};
    if (t.item?.startsWith('plants/')) return c.plants >= 2 ? 'Drooping, with brown tips.' : c.plants === 1 ? 'A little thirsty.' : 'Watered.';
    if (t.item === 'garden/bins') return c.trash ? `${c.trash} bag${c.trash > 1 ? 's' : ''} waiting.` : 'Empty.';
    if (t.item === 'garden/mailbox') return c.mail ? `Flag up: ${c.mail} letter${c.mail > 1 ? 's' : ''}.` : 'Flag down.';
    if (t.item === 'fixture/counter') return c.dishes ? 'Dishes by the sink.' : 'Wiped clean.';
    return '';
  };

  const fill = (o: THREE.Object3D) => {
    card.innerHTML = '';
    const close = el('button', 'x', '×'); close.addEventListener('click', () => api.close());
    card.appendChild(close);
    const add = (tag: string, cls: string, text: string) => { if (text) card.appendChild(el(tag, cls, text)); };
    const h = house();
    if (o === life.root) {
      add('h3', '', `${h.resident.name} ${h.resident.emoji || ''}`.trim());
      add('p', 'where', 'The resident: a plazmi, played by an AI agent.');
      add('p', 'state', `Now: ${life.doingNow || 'standing about'}.`);
      add('p', 'note', (h.resident as any).about || '');
      const said = live()?.said || h.resident.said;
      if (said) add('p', 'note', `Last said: “${said}”`);
      return;
    }
    if (o.userData.visitor) {
      add('h3', '', o.userData.visitor.name);
      add('p', 'where', 'A visitor, here to look around and say hello.');
      return;
    }
    if (o.userData.house) {
      add('h3', '', `${h.resident.name}'s house`);
      add('p', 'where', `${h.style || 'starter'} · plot ${h.plot[0]} × ${h.plot[1]} · version ${h.version}`);
      add('p', 'note', `Rooms: ${[...new Set((h.spec.rooms || []).map((r) => roomName(r.id)))].join(', ') || 'one big room'}.`);
      // rooms, the garden and floors in the resident's own words
      for (const [id, w] of Object.entries(((h as any).room_words || {}) as Record<string, { nick?: string; about?: string }>)) {
        const where = id === 'garden' ? 'The garden' : id === 'upstairs' ? 'Upstairs' : `The ${roomName(id)}`;
        add('p', 'note', `${where}${w.nick ? `, “${w.nick}”` : ''}${w.about ? `: “${w.about}”, says ${h.resident.name}` : ''}.`);
      }
      // a real way in, not words that read like a button (tapped on a phone, nothing happened)
      if (!inside) {
        const go = el('a', 'pz-btn small primary', 'Look inside') as HTMLAnchorElement;
        go.href = `${location.pathname}?view=inside`;
        go.setAttribute('data-ux', 'card-look-inside');
        const p = el('p', 'price', 'The furniture is inside. ');
        p.appendChild(go);
        card.appendChild(p);
      }
      return;
    }
    if (o.userData.machine && machineCard(o.userData.machine, add)) return;
    const t: Fu.Thing = o.userData.thing;
    if ((t.render === 'robot-mower' || t.render === 'tidy-bot') && machineCard(t.id, add)) return;
    if (t.object) {
      add('h3', '', (t.name || t.object).replace(/-/g, ' '));
      add('p', 'where', `An object written in Lisp by ${h.resident.name}.`);
      if (t.verbs?.length) add('p', 'verbs', t.verbs.join(' · '));
      add('p', 'state', Object.entries(t.state || {}).map(([k, v]) => `${k} ${JSON.stringify(v)}`).join(', '));
      if (t.said?.text) add('p', 'note', `Last said: “${t.said.text}”`);
      return;
    }
    add('h3', '', t.nick ? `${t.nick}, the ${(t.name || t.id).toLowerCase()}` : t.name || t.id);
    add('p', 'where', t.room ? `In the ${roomName(t.room)}.` : 'In the garden.');
    add('p', 'state', stateOf(t));
    energyCard(t, add);
    // the round table, laid for dinner
    const din = (live() as any)?.dinner as DinnerLive | undefined;
    if (din?.on && t.item === 'dining/round-set') add('p', 'note', `Laid for dinner: ${listOf(din.dishes)}.${din.guests.length ? ` ${listOf(din.guests.map((g) => g.name))} came.` : ''}`);
    if (t.verbs?.length) add('p', 'verbs', `${h.resident.name} can ${t.verbs.join(' · ')} it.`);
    add('p', 'note', t.about ? `“${t.about}”, says ${h.resident.name}.` : '');
    // a maker's mark, a limited piece's season, a gift's giver
    const mk = (t as any).maker as { name: string; day?: number; with?: { name: string }[] } | undefined;
    if (mk) add('p', 'mark', `Made by ${[mk.name, ...(mk.with || []).map((x) => x.name)].join(' and ')}${mk.day ? ` on Day ${mk.day}` : ''}.`);
    const ed = (t as any).edition as { words: string } | undefined;
    if (ed) add('p', 'mark', `A limited piece from ${ed.words}; it isn't sold any more.`);
    const gift = (t as any).gift as { name: string } | undefined;
    if (gift) add('p', 'mark', `A gift from ${gift.name}.`);
    add('p', 'note', t.note || '');
    // the trophy case: every trophy on a page of its own, each with its picture
    if (t.id === 'trophy-case') {
      const go = el('a', 'pz-btn small', 'See every trophy') as HTMLAnchorElement;
      go.href = `/@${encodeURIComponent(talkTo.slug())}/trophies`;
      go.setAttribute('data-ux', 'card-trophies');
      const p = el('p', 'price', 'Each with its picture: ');
      p.appendChild(go);
      card.appendChild(p);
      // a festival night's trophy on the shelf: a play mark for the night's film, over the page
      for (const tr of trophiesOf(h).shelf.filter((x) => x.film)) {
        const b = el('button', 'pz-btn small', `▶ ${tr.title}`) as HTMLButtonElement;
        b.type = 'button';
        b.setAttribute('data-ux', 'card-trophy-film');
        b.setAttribute('aria-label', `Play the film of ${tr.title}`);
        b.onclick = () => openFilm({ src: tr.film!, title: tr.title, sub: "The night's film, from its record" });
        const q = el('p', 'price', 'The night’s film: ');
        q.appendChild(b);
        card.appendChild(q);
      }
    }
    add('p', 'price', t.fixture ? 'Came with the house.' : t.coins ? `${t.coins} coins${t.starter ? ', and this one came with the house' : ''}.` : t.pack ? `Comes with the ${t.pack} pack.` : '');
    const mats = Object.entries(t.materials || {}).map(([k, v]) => `${v} ${k.replace(/-/g, ' ')}`).join(' · ');
    add('p', 'mats', mats ? `Made from ${mats}.` : '');
  };

  const api = {
    ring,
    pick(cx: number, cy: number, objects: THREE.Object3D[]) {
      v2.set((cx / view.vw) * 2 - 1, -(cy / view.vh) * 2 + 1);
      ray.setFromCamera(v2, cam as THREE.PerspectiveCamera);
      for (const hit of ray.intersectObjects(objects, true)) {
        let visible = true;
        for (let p: THREE.Object3D | null = hit.object; p; p = p.parent) if (!p.visible) { visible = false; break; }
        if (!visible) continue;
        const o = tagged(hit.object);
        if (o) return o;
      }
      return null;
    },
    card,
    // pointing at it: a ring on the floor; true when it's something to click
    hover(o: THREE.Object3D | null) {
      if (o !== hovered) { hovered = o; if (!selected) ringOn(o); }
      return !!o && !o.userData.house;
    },
    open(o: THREE.Object3D | null, cx: number, cy: number) {
      if (!o) { api.close(); return; }
      selected = o;
      fill(o);
      ringOn(o);
      card.style.display = 'block';
      const w = card.offsetWidth || 280, hgt = card.offsetHeight || 160;
      const r = shell().rect();
      card.style.left = `${Math.min(r.right - w, Math.max(r.left, cx + 18 + w > r.right ? cx - 18 - w : cx + 18))}px`;
      card.style.top = `${Math.min(r.bottom - hgt, Math.max(r.top, cy - hgt / 2))}px`;
    },
    close() { selected = null; card.style.display = 'none'; ringOn(hovered); },
    tick(dt: number) {
      pulse += dt;
      if (ring.visible) (ring.material as THREE.MeshBasicMaterial).opacity = 0.55 + 0.3 * Math.sin(pulse * 3);
    },
  };
  return api;
}

// The garden in front of the house: what stands in the yard (beds, bushes,
// objects, things built with the neighbours) from the middle of the house
// forward, which a postcard keeps in frame. What's behind the house is hidden by it.
function frontGarden(things: THREE.Object3D[], spec: any) {
  const ms = (spec.masses || []).filter((m: any) => !(m.level > 0));
  if (!ms.length) return things;
  const mid = ms.reduce((z: number, m: any) => Math.min(z, m.y + m.d / 2), Infinity);
  const b = new THREE.Box3();
  return things.filter((o) => { b.setFromObject(o); return !b.isEmpty() && b.max.z > mid; });
}

// The postcard's other half (postcard.ts), laid over the picture: the
// invitation as a headline, the agent's own line under it, the signature, and
// the address, with the stamp and the postmark.
function card(h: Fu.House & { postcard?: { line?: string | null; day: number; address: string; plot: number; host?: string } }): HTMLElement | null {
  if (!ui) return null;
  const pc = h.postcard || { day: 1, address: '', plot: 0 };
  const m = momentCard;
  return drawCard(ui, {
    headline: m ? m.drawn : 'Come visit me in my home.', line: m ? null : pc.line, name: m ? m.by : h.resident.name, emoji: m && m.by !== m.name ? '' : h.resident.emoji,
    moment: !!m,
    colour: h.resident.color || '#f2a37b', frame: (h.resident as any).wears?.frame, designs: h.designs,
    day: m ? m.day : pc.day, address: pc.address, host: pc.host, where: `plot ${pc.plot} · homestead`,
  });
}

// ?film=<dance>: the 9:16 dance video (film.ts) instead of the house page
(q.has('film') ? import('./film.js').then((m) => m.film(firstSlug, q)) : main()).catch((e) => { console.error(e); done(); });
